/**
 * Public parent packet — token auth. Plain letter text (API form_text) + fillable fields; no in-flow PDF embeds.
 */
import React, {
  useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef,
} from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  Divider,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { API_BASE_URL } from '../services/api';
import {
  CALENDAR_ICS_BUTTON_LABEL,
  isCalendarPacketForm,
  parentCalendarIcsHref,
  externalPdfLinkProps,
  ROCKLAND_DATE_LIST_IMAGE_PATH,
  parentCalendarPdfHref,
  WOODMERE_SUMMER_ICS_PATH,
  WOODMERE_SUMMER_PDF_PATH,
} from './parentPacketCalendar';
import PacketFormIndex from './PacketFormIndex';
import PhysicianFormsDownload from './PhysicianFormsDownload';
import ParentPacketLayout from './ParentPacketLayout';
import { HASC_BLUE } from './parentPacketBrand';
import {
  HANDBOOK_OPENED_EVENT,
  markHandbookOpened,
  parentHandbookPdfHref,
  readHandbookOpened,
} from './parentPacketHandbook';
import NaToggle, { getPacketSiteSlug, isNaToggleEnabledSite } from './NaToggle';
import Fa16SocialHistoryFields, { initialFa16Fields } from './Fa16SocialHistoryFields';
import { ParentPacketCommunicationConsentFields } from './parentPacketCommunicationConsent';
import Fa17FeedingQuestionnaireFields, { initialFa17Fields } from './Fa17FeedingQuestionnaireFields';
import Fa12SocialHistoryFields, {
  initialFa12FieldsFromPacket,
  buildFa12SubmitPayload,
} from './Fa12SocialHistoryFields';
import {
  normalizeFa03BusRows,
  submitFieldsValid,
  validateForSubmit,
  packetFieldId,
  shouldShowRequiredFieldsCaption,
  fourteenthAveInfoAckRequired,
  resolveForm07PreferredPhone,
} from './parentPacketValidation';
import { confirmAgreedLabel } from './parentPacketConfirmLabel';
import {
  forwardButtonLabel,
  firstIncompleteFormIndex,
  remainingIncompleteForms,
  isForwardDisabled,
} from './parentPacketNavigation';
import { formatPhoneInput } from './packetFieldValidation';

const FA08_AFFIRM_KEYS = [
  'consent_video',
  'consent_video_behind',
  'consent_audio',
  'consent_photo',
  'consent_photo_blurred',
  'consent_social_media',
];

const YES_NO_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
];

const YES_NO_LOWER_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

/** Map a free-text/boolean glasses value to the 'Yes'/'No' select options. */
function normalizeYesNo(value) {
  if (value === true) return 'Yes';
  if (value === false) return '';
  const v = String(value || '').trim().toLowerCase();
  if (v === 'yes' || v === 'y' || v === 'true') return 'Yes';
  if (v === 'no' || v === 'n' || v === 'false') return 'No';
  return '';
}

// Tri-state region pick-list (NY metro + neighbors) for address state fields.
const US_STATE_OPTIONS = [
  'NY', 'NJ', 'CT', 'PA', 'FL', 'MA', 'MD', 'CA',
];

/** form_1 / form_01 → 1 */
function normalizePacketWantFormId(want) {
  const raw = (want || '').trim().replace(/-/g, '_');
  if (!raw) return '';
  const m = /^form_(\d+)$/i.exec(raw);
  if (m) return `form_${parseInt(m[1], 10).toString().padStart(2, '0')}`;
  return raw;
}

/** form_1 / form_01 → 1 */
function formIdToNum(formId) {
  const m = /^form_(\d+)$/i.exec(formId || '');
  return m ? parseInt(m[1], 10) : -1;
}

function isFaForm(formId) {
  return typeof formId === 'string' && formId.startsWith('fa_');
}

function isWmForm(formId) {
  return typeof formId === 'string' && formId.startsWith('wm_');
}

function packetDraftStorageKey(token, formId) {
  return `packet_draft_${token}_${formId}`;
}

function familyFlagsFromPacket(packet) {
  const p = packet || {};
  return {
    is_single_parent: !!p.is_single_parent,
    has_mother: p.has_mother !== false,
    has_father: p.has_father !== false,
    has_guardian: p.has_guardian === true,
  };
}

function familyStructureEditingLabel(flags) {
  const bits = [];
  if (flags.has_mother) bits.push('Mother');
  if (flags.has_father) bits.push('Father');
  if (flags.has_guardian) bits.push('Guardian');
  return bits.join(' — ') || '…';
}

function CalendarFormActions({ formId, siteSlug, token, calendarLinks, rocklandCalendarLinks }) {
  if (!isCalendarPacketForm(formId, siteSlug)) return null;
  const isRockland = siteSlug === 'rockland';
  const pdfHref = isRockland
    ? null
    : parentCalendarPdfHref({ siteSlug, token, apiBaseUrl: API_BASE_URL });
  const icsHref = parentCalendarIcsHref({ token, apiBaseUrl: API_BASE_URL });
  const staticLinks = (Array.isArray(calendarLinks) && calendarLinks.length
    ? calendarLinks
    : (Array.isArray(rocklandCalendarLinks) && rocklandCalendarLinks.length ? rocklandCalendarLinks : []));
  if (!pdfHref && !icsHref && !staticLinks.length && !isRockland) return null;
  const generatedLabel = siteSlug === '14th-ave'
    ? 'View school calendar (session dates)'
    : 'View school calendar (PDF)';
  return (
    <>
      {isRockland && (
        // Inline image (mobile-safe): in-app email browsers can't render PDFs,
        // so the schedule itself is shown here; the PDF stays as a download.
        <Box
          component="img"
          src={ROCKLAND_DATE_LIST_IMAGE_PATH}
          alt="HASC Rockland school calendar 2026-2027: key dates, holidays, and session days"
          loading="lazy"
          sx={{
            mt: 2,
            display: 'block',
            width: '100%',
            maxWidth: 820,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        />
      )}
    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {staticLinks.map((link) => (
        <Button
          key={link.id}
          variant={isRockland && link.id === 'date_list' ? 'outlined' : 'contained'}
          size="medium"
          startIcon={<PictureAsPdfIcon />}
          {...externalPdfLinkProps(link.href)}
        >
          {isRockland && link.id === 'date_list' ? 'Download the calendar (PDF)' : link.label}
        </Button>
      ))}
      {pdfHref && (
        <Button
          variant="outlined"
          size="medium"
          startIcon={<PictureAsPdfIcon />}
          {...externalPdfLinkProps(pdfHref)}
        >
          {generatedLabel}
        </Button>
      )}
      {icsHref && (
        <Button
          variant="outlined"
          size="medium"
          startIcon={<EventAvailableIcon />}
          {...externalPdfLinkProps(icsHref)}
        >
          {CALENDAR_ICS_BUTTON_LABEL}
        </Button>
      )}
      {formId === 'wm_16' && (
        <>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<PictureAsPdfIcon />}
            {...externalPdfLinkProps(WOODMERE_SUMMER_PDF_PATH)}
          >
            View summer 2026 calendar (PDF)
          </Button>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<EventAvailableIcon />}
            {...externalPdfLinkProps(WOODMERE_SUMMER_ICS_PATH)}
          >
            Add summer dates to calendar (.ics)
          </Button>
        </>
      )}
    </Box>
    </>
  );
}

/**
 * 55th Street (bs_*) informational letters / physician-completed PDFs with no
 * parent-entry fields. These show the letter text + (for PDF-backed ones) a
 * "view PDF" button, then a "Mark as reviewed & continue" button — never the
 * generic confirm_agreed checkbox. Matches backend is_informational_form().
 */
const BS_INFORMATIONAL_FORMS = new Set([
  'bs_01', 'bs_02', 'bs_03', 'bs_06', 'bs_07', 'bs_14', 'bs_15', 'bs_16',
]);

/** Form slots that are PDF-backed (handbook / physician health form) and need a "view PDF" button that opens in a new tab. */
const HANDBOOK_PDF_FORMS = new Set(['bs_15', 'wm_15']);
const HEALTH_PDF_FORMS = new Set(['bs_14', 'wm_08', 'wm_14']);
/** Rockland informational letters with generated PDFs (form_text + view PDF). */
const ROCKLAND_LETTER_PDF_FORMS = new Set(['form_01', 'form_02', 'form_03', 'form_04']);

function MealsFormNotice({ formId, token }) {
  if (formId !== 'form_17') return null;
  const href = `${API_BASE_URL}/packet/${encodeURIComponent(token)}/${encodeURIComponent(formId)}/pdf/`;
  return (
    <>
      <Alert severity="info" sx={{ mt: 2 }}>
        The official NY State free/reduced-price meals application (including breakfast program eligibility)
        must be completed on the printed PDF. Online review here only confirms HASC&apos;s pre-filled school
        information and your signature — household income, student names, and meal eligibility answers are
        not collected online and will not appear in your signed packet unless you fill them on the PDF by hand.
      </Alert>
      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="outlined"
          size="medium"
          startIcon={<PictureAsPdfIcon />}
          {...externalPdfLinkProps(href)}
        >
          View meals application (PDF)
        </Button>
      </Box>
    </>
  );
}

function Fa05MedicalFormLink({ formId, token }) {
  // fa_05 letter tells parents to "use the link below this letter to open or
  // print the full blank form for your doctor" — render that link (Fay QA #2).
  if (formId !== 'fa_05') return null;
  const href = `${API_BASE_URL}/packet/${encodeURIComponent(token)}/${encodeURIComponent(formId)}/pdf/`;
  return (
    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      <Button
        variant="outlined"
        size="medium"
        startIcon={<PictureAsPdfIcon />}
        {...externalPdfLinkProps(href)}
      >
        Open / print the blank medical form for your doctor (PDF)
      </Button>
    </Box>
  );
}

function InformationalPdfActions({ formId, siteSlug, token }) {
  const isHandbook = HANDBOOK_PDF_FORMS.has(formId);
  const isHealth = HEALTH_PDF_FORMS.has(formId);
  const isRocklandLetter = ROCKLAND_LETTER_PDF_FORMS.has(formId);
  if (!isHandbook && !isHealth && !isRocklandLetter) return null;
  const href = isHandbook
    ? parentHandbookPdfHref({ siteSlug, token, apiBaseUrl: API_BASE_URL })
    : `${API_BASE_URL}/packet/${encodeURIComponent(token)}/${encodeURIComponent(formId)}/pdf/`;
  if (!href) return null;
  let label = 'View form (PDF)';
  if (isHandbook) label = 'View Parent Handbook (PDF)';
  else if (isHealth && formId === 'bs_14') label = 'View CH-205 health exam form (PDF)';
  else if (formId === 'wm_08') label = 'View OCFS medication authorization (PDF)';
  else if (formId === 'wm_14') label = 'View NYS health exam form (PDF)';
  else if (formId === 'form_04') label = 'View immunization requirements (PDF)';
  return (
    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      <Button
        variant="outlined"
        size="medium"
        startIcon={<PictureAsPdfIcon />}
        {...externalPdfLinkProps(href)}
      >
        {label}
      </Button>
    </Box>
  );
}

function HandbookDownloadCard({ siteSlug, token, variant = 'default' }) {
  const href = parentHandbookPdfHref({ siteSlug, token, apiBaseUrl: API_BASE_URL });
  if (!href || !siteSlug || siteSlug === '14th-ave') return null;
  const isHasc = variant === 'hascBlue';
  const handleOpen = () => {
    // Same unlock as form_19's in-form button — either entry point must clear the gate.
    // Navigation itself happens via the anchor href (mobile-safe), not window.open.
    markHandbookOpened(token);
  };
  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        p: { xs: 1.5, md: 2 },
        bgcolor: isHasc ? HASC_BLUE.highlight : '#f0f7ff',
        borderColor: isHasc ? HASC_BLUE.primary : 'primary.light',
        borderWidth: isHasc ? 1.5 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <MenuBookIcon
          sx={{ mt: 0.25, color: isHasc ? HASC_BLUE.primary : undefined }}
          color={isHasc ? undefined : 'primary'}
          aria-hidden="true"
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '0.95rem', md: '1rem' } }}>
            Download the Parent Handbook
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            View or print the HASC Parent Handbook for this school year.
          </Typography>
          <Button
            variant={isHasc ? 'outlined' : 'contained'}
            color={isHasc ? undefined : 'primary'}
            onClick={handleOpen}
            {...externalPdfLinkProps(href)}
            fullWidth={isHasc}
            sx={isHasc ? {
              borderColor: HASC_BLUE.primary,
              color: HASC_BLUE.primary,
              fontWeight: 600,
              py: { xs: 1.1, md: 0.75 },
              '&:hover': {
                borderColor: HASC_BLUE.primaryHover,
                bgcolor: '#fff',
                color: HASC_BLUE.primaryHover,
              },
            } : undefined}
          >
            View Parent Handbook (PDF)
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

function isInformationalPacketForm(formId) {
  if (isFaForm(formId)) {
    return formId === 'fa_01' || formId === 'fa_02' || formId === 'fa_14' || formId === 'fa_15';
  }
  if (isWmForm(formId)) {
    return formId === 'wm_08' || formId === 'wm_14' || formId === 'wm_15' || formId === 'wm_16';
  }
  if (typeof formId === 'string' && formId.startsWith('bs_')) {
    return BS_INFORMATIONAL_FORMS.has(formId);
  }
  const n = formIdToNum(formId);
  return (n >= 1 && n <= 6) || n === 16 || n === 17;
}

/** Full letter text (API form_text); Rockland 01–06 + calendar/meals/OCFS; 14th Ave fa_01, fa_02, fa_14, fa_15; 55th bs_* letters */
function informationalTextOnlyForm(formId) {
  if (formId === 'fa_01' || formId === 'fa_02' || formId === 'fa_14' || formId === 'fa_15') return true;
  if (isWmForm(formId)) {
    return formId === 'wm_08' || formId === 'wm_14' || formId === 'wm_15' || formId === 'wm_16';
  }
  if (typeof formId === 'string' && formId.startsWith('bs_')) {
    return BS_INFORMATIONAL_FORMS.has(formId);
  }
  const n = formIdToNum(formId);
  return [1, 2, 3, 4, 5, 6, 16, 17].includes(n);
}

function formHasParentFields(form) {
  if (!form) return false;
  if (form.has_parent_fields === true) return true;
  return !isInformationalPacketForm(form.form_id);
}

const PLACEHOLDER_EMERGENCY_CONTACT_NAMES = new Set([
  'parent/guardian of student',
  'parent/guardian',
]);

function sanitizeEmergencyContactPrefill(name) {
  const n = (name || '').trim();
  if (!n) return '';
  const low = n.toLowerCase();
  const collapsed = low.replace(/\s*\/\s*/g, '/').replace(/\s+/g, ' ').trim();
  if (PLACEHOLDER_EMERGENCY_CONTACT_NAMES.has(low)) return '';
  if (PLACEHOLDER_EMERGENCY_CONTACT_NAMES.has(collapsed)) return '';
  if (collapsed.startsWith('parent/guardian of')) return '';
  return n;
}

function boolToYesNo(v) {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

export function initialFieldsForForm(formId, packet) {
  const p = packet || {};
  const base = { confirm_agreed: false };
  const mp = p.medical_profile || {};
  const ecs = p.emergency_contacts || [];
  const pps = p.pickup_persons || [];
  const consent = p.consent || {};
  const rows = p.allergy_rows || [];

  switch (formId) {
    case 'form_07':
      return {
        ...base,
        child_gender: p.student_gender || '',
        mother_name: p.mother_name || '',
        mother_cell: p.mother_cell || '',
        mother_row_primary: false,
        mother_row_guardian: false,
        father_name: p.father_name || '',
        father_cell: p.father_cell || '',
        father_row_primary: false,
        father_row_guardian: false,
        home_address: p.home_address || '',
        home_phone: p.home_phone || '',
        preferred_phone: p.preferred_phone || p.parent_cell || '',
        alternate_address: p.alternate_address || '',
        email: p.parent_email || '',
        email_viewed_daily: p.email_viewed_daily === true ? 'yes' : p.email_viewed_daily === false ? 'no' : '',
        mother_work_address: p.mother_work_address || '',
        mother_work_phone: p.mother_work_phone || '',
        father_work_address: p.father_work_address || '',
        father_work_phone: p.father_work_phone || '',
        allergies: p.allergies || '',
        medication_school: mp.medications_text || '',
        medication_home: '',
        ec1_name: sanitizeEmergencyContactPrefill(ecs[0]?.name || ''),
        ec1_relationship: ecs[0]?.relationship || '',
        ec1_phone: ecs[0]?.phone || '',
        ec1_address: ecs[0]?.address || '',
        ec2_name: ecs[1]?.name || '',
        ec2_relationship: ecs[1]?.relationship || '',
        ec2_phone: ecs[1]?.phone || '',
        ec2_address: ecs[1]?.address || '',
        pediatrician_name: p.pediatrician_name || '',
        pediatrician_phone: p.pediatrician_phone || '',
        dentist_name: p.dentist_name || '',
        dentist_phone: p.dentist_phone || '',
        specialist_name: p.specialist_name || '',
        specialist_specialty: p.specialist_specialty || '',
        specialist_phone: p.specialist_phone || '',
        preferred_hospital: p.preferred_hospital || '',
        insurance_id: p.child_insurance_id || '',
        insurance_type: p.insurance_type || '',
        guardian_name: p.guardian_name || '',
        guardian_cell: p.guardian_cell || '',
        guardian_work_phone: p.guardian_work_phone || '',
        guardian_home_phone: p.guardian_home_phone || '',
        guardian_email: p.guardian_email || '',
        guardian_work_address: p.guardian_work_address || '',
        guardian_relationship: p.guardian_relationship || '',
      };
    case 'form_08':
      return {
        ...base,
        visual_impairment: boolToYesNo(mp.has_visual_impairment),
        glasses: boolToYesNo(mp.wears_glasses),
        ear_infection: boolToYesNo(mp.has_ear_infection),
        ear_tubes: boolToYesNo(mp.has_ear_tubes),
        hearing_impairment: boolToYesNo(mp.has_hearing_impairment),
        assistive_hearing_device: '',
        assistive_hearing_right: false,
        assistive_hearing_left: false,
        seizure: boolToYesNo(mp.has_seizure_disorder),
        gtube: boolToYesNo(mp.has_gtube),
        asthma: boolToYesNo(mp.has_asthma),
        respiratory_compromise: boolToYesNo(mp.has_respiratory_compromise),
        cardiac_compromise: boolToYesNo(mp.has_cardiac_compromise),
        skin_condition: boolToYesNo(mp.has_skin_condition),
        dental_condition: boolToYesNo(mp.has_dental_condition),
        tracheostomy: boolToYesNo(mp.has_tracheostomy),
        oxygen: boolToYesNo(mp.has_oxygen),
        oximeter_monitoring: boolToYesNo(mp.requires_oximeter_monitoring),
        can_food_by_mouth: boolToYesNo(mp.can_have_food_by_mouth),
        can_food_by_mouth_liquids: boolToYesNo(mp.food_by_mouth_liquids),
        can_food_by_mouth_solids: boolToYesNo(mp.food_by_mouth_solids),
        swallow_study: '',
        seizure_type_frequency: '',
        gtube_feeding_instructions: '',
        problems_eating_drinking: mp.problems_eating_drinking === true ? 'yes' : mp.problems_eating_drinking === false ? 'no' : '',
        problems_eating_drinking_specify: mp.problems_eating_drinking_specify || '',
        medications: mp.medications_text || '',
        other_medical: mp.other_medical_notes || '',
        past_hospitalizations: '',
      };
    case 'form_09': {
      const foodFromHome = (mp.food_from_home || '').toString().toLowerCase();
      const liquidType = (mp.liquid_type || '').toString().toLowerCase();
      return {
        ...base,
        allergy_rows: rows.length > 0
          ? rows.map((r) => ({
            allergen: r.allergen || '',
            eat: r.reacts_to_eat || false,
            touch: r.reacts_to_touch || false,
            smell: r.reacts_to_smell || false,
            reaction: r.reaction_description || '',
            requires_anaphylaxis_plan: !!r.requires_anaphylaxis_plan,
          }))
          : Array(5).fill(null).map(() => ({
            allergen: '', eat: false, touch: false, smell: false, reaction: '',
            requires_anaphylaxis_plan: false,
          })),
        food_from_home_yn: foodFromHome === 'yes' || foodFromHome === 'no' ? foodFromHome : '',
        food_liquid_thin: liquidType.includes('thin'),
        food_liquid_thickened: liquidType.includes('thicken'),
        liquid_thickness_detail: '',
        food_chewable_solids: liquidType.includes('chewable'),
        food_pureed: liquidType.includes('pureed'),
        has_no_allergies: false,
        has_allergies: rows.length > 0,
        dairy_no_milk_or_food: false,
        dairy_no_milk_some_food: false,
        dairy_avoid_occasional: false,
        dairy_allowed_foods: '',
        additional_comments: '',
      };
    }
    case 'form_10':
      return {
        ...base,
        nickname: p.student_nickname || '',
        hair_color: p.student_hair_color || '',
        eye_color: p.student_eye_color || '',
        glasses: normalizeYesNo(p.student_glasses),
        physical_description: p.student_physical_description || '',
        usual_behavior: p.student_usual_behavior || '',
        communication_ability: p.student_communication_ability || '',
        favorite_locations: p.student_favorite_locations || '',
      };
    case 'form_13':
      return {
        ...base,
        c_classroom: false,
        c_newsletters: false,
        c_hallways: false,
        c_website: false,
        c_flyers: false,
        c_slideshow_building: false,
        c_class_list: false,
        c_yearbook: false,
        // A2P two-box consent (2026-06-16-a2p-v1); both optional, default unchecked.
        sms_consent: false,
        voice_consent: false,
      };
    case 'form_14':
      return {
        ...base,
        p1_name: pps[0]?.name || '',
        p1_relationship: pps[0]?.relationship || '',
        p1_phone: pps[0]?.phone || '',
        p2_name: pps[1]?.name || '',
        p2_relationship: pps[1]?.relationship || '',
        p2_phone: pps[1]?.phone || '',
        p3_name: pps[2]?.name || '',
        p3_relationship: pps[2]?.relationship || '',
        p3_phone: pps[2]?.phone || '',
      };
    case 'form_15':
      return {
        ...base,
        release_consent_ack: false,
        release_consent_decline: false,
      };
    case 'form_20':
    case 'fa_18':
    case 'wm_17':
    case 'bs_17':
      return {
        ...base,
        opwdd_eligible: '',
        receiving_respite: '',
        contact_after_school: '',
        phi_authorize: false,
        phi_decline: false,
      };
    case 'form_11':
    case 'form_18':
      return { ...base, confirm_policy: false };
    case 'form_12':
      return {
        ...base,
        creams_consent_ack: false,
        creams_consent_decline: false,
        creams_avoid_notes: '',
      };
    case 'form_19':
      return {
        ...base,
        handbook_received_ack: false,
        handbook_illness_policy_ack: false,
        handbook_therapy_makeup_ack: false,
      };
    case 'fa_03': {
      const bus = [];
      for (let i = 0; i < 2; i += 1) {
        const rawName = (pps[i]?.name || '').trim();
        const parts = rawName.split(/\s+/).filter(Boolean);
        bus.push({
          first_name: parts[0] || '',
          last_name: parts.slice(1).join(' ') || '',
          relationship: '',
          address: pps[i]?.address || '',
          phone: pps[i]?.phone || '',
        });
      }
      return { ...base, bus_pickup_rows: bus };
    }
    case 'fa_04':
      return {
        ...base,
        mother_name: p.mother_name || '',
        mother_cell: p.mother_cell || '',
        father_name: p.father_name || '',
        father_cell: p.father_cell || '',
        home_address: p.home_address || '',
        email: p.parent_email || '',
        allergies: p.allergies || '',
        medications_school: mp.medications_text || '',
        ec1_name: sanitizeEmergencyContactPrefill(ecs[0]?.name || ''),
        ec1_relationship: ecs[0]?.relationship || '',
        ec1_phone: ecs[0]?.phone || '',
        ec2_name: ecs[1]?.name || '',
        ec2_relationship: ecs[1]?.relationship || '',
        ec2_phone: ecs[1]?.phone || '',
        ec3_name: sanitizeEmergencyContactPrefill(ecs[2]?.name || ''),
        ec3_relationship: ecs[2]?.relationship || '',
        ec3_phone: ecs[2]?.phone || '',
        pediatrician_name: p.pediatrician_name || '',
        pediatrician_phone: p.pediatrician_phone || '',
        dentist_name: p.dentist_name || '',
        dentist_phone: p.dentist_phone || '',
        preferred_hospital: p.preferred_hospital || '',
        insurance_id: p.child_insurance_id || '',
        insurance_type: p.insurance_type || '',
        health_additional_notes: mp.other_medical_notes || '',
        emergency_treatment_ack: false,
        emergency_treatment_decline: false,
        guardian_name: p.guardian_name || '',
        guardian_cell: p.guardian_cell || '',
        guardian_work_phone: p.guardian_work_phone || '',
        guardian_home_phone: p.guardian_home_phone || '',
        guardian_email: p.guardian_email || '',
        guardian_work_address: p.guardian_work_address || '',
        guardian_relationship: p.guardian_relationship || '',
      };
    case 'fa_05':
      return {
        ...base,
        child_first_name: p.student_first_name || '',
        child_last_name: p.student_last_name || '',
        child_dob: p.student_dob || '',
        resides_parents: false,
        resides_parents_names: '',
        resides_guardian: false,
        guardian_name: p.guardian_name || '',
        resides_residence: false,
        residence_name: '',
        student_address: p.home_address || '',
        student_city: '',
        student_state: '',
        student_zip: '',
        mother_name: p.mother_name || '',
        mother_cell: p.mother_cell || '',
        father_name: p.father_name || '',
        father_cell: p.father_cell || '',
        email: p.parent_email || '',
        parent_phone: p.parent_cell || '',
        guardian_cell: p.guardian_cell || '',
        guardian_work_phone: p.guardian_work_phone || '',
        guardian_home_phone: p.guardian_home_phone || '',
        guardian_email: p.guardian_email || '',
        guardian_work_address: p.guardian_work_address || '',
        guardian_relationship: p.guardian_relationship || '',
        medicaid_number: p.child_insurance_id || '',
        other_insurance_company: '',
        other_insurance_policy: '',
        parent_physical_diagnosis: '',
        parent_intellectual_diagnosis: '',
        allergy_food: '',
        allergy_drug: '',
        allergy_other: '',
        ec1_name: sanitizeEmergencyContactPrefill(ecs[0]?.name || ''),
        ec1_relationship: ecs[0]?.relationship || '',
        ec1_phone: ecs[0]?.phone || '',
        ec2_name: ecs[1]?.name || '',
        ec2_relationship: ecs[1]?.relationship || '',
        ec2_phone: ecs[1]?.phone || '',
        ec3_name: sanitizeEmergencyContactPrefill(ecs[2]?.name || ''),
        ec3_relationship: ecs[2]?.relationship || '',
        ec3_phone: ecs[2]?.phone || '',
        otc_consent_ack: false,
        otc_consent_decline: false,
      };
    case 'fa_06':
      return {
        ...base,
        na_no_meds_or_feedings: false,
        medication_notes: mp.medications_text || '',
        understands_labeled_bottles: false,
      };
    case 'fa_07':
      return {
        ...base,
        pediatrician_name: p.pediatrician_name || '',
        pediatrician_phone: p.pediatrician_phone || '',
        dentist_name: p.dentist_name || '',
        dentist_phone: p.dentist_phone || '',
        preferred_hospital: p.preferred_hospital || '',
        insurance_id: p.child_insurance_id || '',
        insurance_type: p.insurance_type || '',
        doctor_comm_ack: false,
        doctor_comm_decline: false,
        release_info_ack: false,
        release_info_decline: false,
      };
    case 'fa_08':
      return {
        ...base,
        consent_video: !!consent.consent_video,
        consent_video_behind: !!consent.consent_video_behind,
        consent_audio: !!consent.consent_audio,
        consent_photo: !!consent.consent_photo,
        consent_photo_blurred: !!consent.consent_photo_blurred,
        consent_social_media: !!consent.consent_social_media,
        consent_none: !!consent.consent_none,
      };
    case 'bs_08':
      return {
        ...base,
        consent_none: !!consent.consent_none,
        consent_video: !!consent.consent_video,
        consent_audio: !!consent.consent_audio,
        consent_photo: !!consent.consent_photo,
        consent_school_use: !!consent.consent_school_use,
        consent_website: !!consent.consent_website,
      };
    case 'wm_01':
      return {
        ...base,
        parent_name: p.mother_name || p.father_name || '',
        student_name: `${p.student_first_name || ''} ${p.student_last_name || ''}`.trim(),
        home_phone: p.parent_phone || p.mother_cell || p.father_cell || '',
        alert_cell_phone: p.mother_cell || p.father_cell || '',
        email: p.parent_email || '',
      };
    case 'fa_09':
      return {
        ...base,
        remote_classroom: !!consent.remote_learning_classroom,
        remote_speech: !!consent.remote_learning_speech,
        remote_ot: !!consent.remote_learning_ot,
        remote_pt: !!consent.remote_learning_pt,
        remote_counseling: !!consent.remote_learning_counseling,
        remote_learning_decline: false,
      };
    case 'fa_10':
      return { ...base, trips_ack: false, trips_decline: false };
    case 'fa_11':
      return {
        ...base,
        sc_name: p.service_coordinator_name || '',
        sc_agency: p.service_coordinator_agency || '',
        sc_phone: p.service_coordinator_phone || '',
        sc_not_assigned: false,
      };
    case 'fa_12':
      return {
        ...base,
        ...initialFa12FieldsFromPacket(p.social_history_data || {}),
      };
    case 'fa_16':
      return { ...base, ...initialFa16Fields() };
    case 'fa_17':
      return { ...base, ...initialFa17Fields() };
    case 'wm_02':
      return {
        ...base,
        parent_name: p.mother_name || p.father_name || '',
        medicaid_cin: '',
      };
    case 'wm_13':
      return {
        ...base,
        parent_name: p.mother_name || p.father_name || '',
        medicaid_cin: '',
        medicaid_consent_ack: false,
        medicaid_consent_decline: false,
      };
    case 'wm_03':
      return {
        ...base,
        student_name: `${p.student_first_name || ''} ${p.student_last_name || ''}`.trim(),
        date_of_birth: p.student_dob || '',
        gender: '',
        home_address: p.home_address || '',
        home_city: '',
        home_state: '',
        home_zip: '',
        home_phone: p.parent_phone || p.mother_cell || p.father_cell || '',
        child_lives_with: '',
        mother_name: p.mother_name || '',
        mother_cell: p.mother_cell || '',
        mother_work_phone: p.mother_work_phone || '',
        mother_work_address: p.mother_work_address || '',
        email: p.parent_email || '',
        father_name: p.father_name || '',
        father_cell: p.father_cell || '',
        father_work_phone: p.father_work_phone || '',
        father_work_address: p.father_work_address || '',
        father_email: p.father_email || p.parent_email || '',
        docuseal_recipient: '',
        primary_language: '',
        interpreter_needed: '',
        pediatrician_name: p.pediatrician_name || '',
        pediatrician_phone: p.pediatrician_phone || '',
        allergies: p.allergies || '',
        hospitalizations: '',
        medications_yesno: '',
        medications_detail: '',
        insurance_name: p.insurance_type || '',
        insurance_id: p.child_insurance_id || '',
        insurance_group: '',
        ec1_name: sanitizeEmergencyContactPrefill(ecs[0]?.name || ''),
        ec1_relationship: ecs[0]?.relationship || '',
        ec1_home_phone: '',
        ec1_cell_phone: ecs[0]?.phone || '',
        ec1_address: ecs[0]?.address || '',
        ec1_city: '',
        ec1_state: '',
        ec1_zip: '',
        ec2_name: ecs[1]?.name || '',
        ec2_relationship: ecs[1]?.relationship || '',
        ec2_home_phone: '',
        ec2_cell_phone: ecs[1]?.phone || '',
        ec2_address: ecs[1]?.address || '',
        ec2_city: '',
        ec2_state: '',
        ec2_zip: '',
        ec3_name: sanitizeEmergencyContactPrefill(ecs[2]?.name || ''),
        ec3_relationship: ecs[2]?.relationship || '',
        ec3_home_phone: '',
        ec3_cell_phone: ecs[2]?.phone || '',
        ec3_address: ecs[2]?.address || '',
        ec3_city: '',
        ec3_state: '',
        ec3_zip: '',
      };
    case 'wm_04':
      return {
        ...base,
        student_name: `${p.student_first_name || ''} ${p.student_last_name || ''}`.trim(),
        home_address: p.home_address || '',
        home_phone: p.parent_phone || p.mother_cell || p.father_cell || '',
        insurance_name: p.insurance_type || '',
        ec1_name: sanitizeEmergencyContactPrefill(ecs[0]?.name || ''),
        ec1_relationship: ecs[0]?.relationship || '',
        parent_name: p.mother_name || p.father_name || '',
        gender: '',
      };
    case 'wm_06':
      return {
        ...base,
      };
    case 'wm_05':
      return {
        ...base,
        consent_video: false,
        consent_audio: false,
        consent_photo: false,
        consent_none: false,
      };
    case 'wm_07':
      return {
        ...base,
        student_name: `${p.student_first_name || ''} ${p.student_last_name || ''}`.trim(),
        date_of_birth: p.student_dob || '',
        no_medications: false,
        medication_name: '',
        medication_dosage: '',
        medication_frequency: '',
        medication_2_name: '',
        medication_2_dosage: '',
        medication_2_frequency: '',
      };
    case 'wm_09':
      return {
        ...base,
        parent_name: p.mother_name || p.father_name || '',
        medicaid_cin: '',
        medicaid_consent_ack: false,
        medicaid_consent_decline: false,
      };
    case 'wm_10': {
      const authFields = {};
      for (let i = 1; i <= 4; i += 1) {
        authFields[`auth${i}_name`] = '';
        authFields[`auth${i}_cell`] = '';
        authFields[`auth${i}_vehicle`] = '';
        authFields[`auth${i}_plate`] = '';
      }
      return {
        ...base,
        child_last_name: p.student_last_name || '',
        child_first_name: p.student_first_name || '',
        date_of_birth: p.student_dob || '',
        gender: '',
        home_address: p.home_address || '',
        home_city: '',
        home_zip: '',
        mother_name: p.mother_name || '',
        mother_cell: p.mother_cell || '',
        father_name: p.father_name || '',
        father_cell: p.father_cell || '',
        ...authFields,
      };
    }
    case 'wm_11':
      return {
        ...base,
        student_name: `${p.student_first_name || ''} ${p.student_last_name || ''}`.trim(),
      };
    case 'wm_12':
      return {
        ...base,
        student_name: `${p.student_first_name || ''} ${p.student_last_name || ''}`.trim(),
        date_of_birth: p.student_dob || '',
        allergies: p.allergies || '',
        special_diet: '',
        medical_surgical: '',
        birthmarks_scars: '',
      };
    case 'wm_14':
      return {
        ...base,
        student_name: `${p.student_first_name || ''} ${p.student_last_name || ''}`.trim(),
        date_of_birth: p.student_dob || '',
      };
    default:
      return { ...base };
  }
}

/** 11pt typed/selected text in parent-packet fillable fields (11 × 96/72 ≈ 14.667px; MUI 16px root → 0.9167rem). */
const PACKET_FORM_INPUT_FONT_SX = {
  '& .MuiInputBase-input': { fontSize: '0.9167rem' },
};

export function FormFields(props) {
  return (
    <Box sx={PACKET_FORM_INPUT_FONT_SX} data-packet-form-inputs>
      <FormFieldsInner {...props} />
    </Box>
  );
}

function FormFieldsInner({
  formId, fields, setF, packet, fieldErrors = {}, clearFieldError, readOnly = false,
  token, siteSlug,
  /** 'stack' (default) | 'wmTwoColumn' — Woodmere desktop field grid only. */
  fieldLayout = 'stack',
}) {
  const [handbookOpened, setHandbookOpened] = useState(() => readHandbookOpened(token));
  useEffect(() => {
    setHandbookOpened(readHandbookOpened(token));
    if (typeof window === 'undefined') return undefined;
    const onOpened = (event) => {
      if (event?.detail?.token === token) setHandbookOpened(true);
    };
    window.addEventListener(HANDBOOK_OPENED_EVENT, onOpened);
    return () => window.removeEventListener(HANDBOOK_OPENED_EVENT, onOpened);
  }, [token]);
  const showMother = packet?.has_mother !== false;
  const showFather = packet?.has_father !== false;
  const showGuardian = packet?.has_guardian === true;
  const twoCol = fieldLayout === 'wmTwoColumn';
  const err = (key) => fieldErrors[key];
  const onText = (key) => (e) => {
    if (clearFieldError) clearFieldError(key);
    setF(key, e.target.value);
  };
  const onPhone = (key) => (e) => {
    if (clearFieldError) clearFieldError(key);
    setF(key, formatPhoneInput(e.target.value));
  };
  const onCheck = (key) => (e) => {
    if (clearFieldError) clearFieldError(key);
    setF(key, e.target.checked);
  };
  const reqStar = (
    <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>
  );
  const wrapCell = (node, { fullWidth = false, key } = {}) => {
    if (!twoCol || !node) return node;
    return (
      <Box
        key={key}
        sx={{
          gridColumn: fullWidth ? '1 / -1' : 'auto',
          minWidth: 0,
          '& .MuiTextField-root': { mt: 1, mb: 0.5 },
        }}
      >
        {node}
      </Box>
    );
  };
  /** Wrap a Woodmere form's field tree so paired short fields sit 2-up on desktop. */
  const wmGrid = (children) => {
    if (!twoCol) return children;
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          columnGap: 2,
          alignItems: 'start',
          '& > .MuiTypography-root': { gridColumn: '1 / -1', mt: 1.5 },
          '& > .MuiFormControlLabel-root': { gridColumn: '1 / -1' },
          '& > .MuiFormControl-root': { gridColumn: '1 / -1' },
          '& > .MuiAlert-root': { gridColumn: '1 / -1' },
        }}
      >
        {children}
      </Box>
    );
  };
  const row = (label, key, props = {}, opts = {}) => {
    const { required: isRequired, phone: isPhone, fullWidth: forceFull } = opts;
    const emsg = err(key);
    const { inputProps: extraInputProps, ...fieldProps } = props;
    const isMultiline = !!fieldProps.multiline;
    const field = (
      <TextField
        id={packetFieldId(key)}
        fullWidth
        required={!!isRequired && !readOnly}
        label={label}
        value={fields[key] ?? ''}
        onChange={isPhone ? onPhone(key) : onText(key)}
        margin={twoCol ? 'dense' : 'normal'}
        size={twoCol ? 'medium' : 'small'}
        error={!!emsg}
        helperText={emsg || (isPhone && !emsg ? 'Enter a 10-digit phone number' : undefined)}
        InputProps={{ readOnly }}
        disabled={readOnly}
        inputProps={{ ...extraInputProps, 'data-field': key }}
        {...fieldProps}
      />
    );
    return wrapCell(field, { fullWidth: forceFull || isMultiline, key });
  };
  const selectRow = (label, key, options, opts = {}) => {
    const { required: isRequired, fullWidth: forceFull } = opts;
    const emsg = err(key);
    const field = (
      <TextField
        id={packetFieldId(key)}
        select
        fullWidth
        required={!!isRequired && !readOnly}
        label={label}
        value={fields[key] ?? ''}
        onChange={onText(key)}
        margin={twoCol ? 'dense' : 'normal'}
        size={twoCol ? 'medium' : 'small'}
        error={!!emsg}
        helperText={emsg || undefined}
        InputProps={{ readOnly }}
        disabled={readOnly}
        inputProps={{ 'data-field': key }}
      >
        {options.map((opt) => {
          const value = typeof opt === 'string' ? opt : opt.value;
          const text = typeof opt === 'string' ? opt : opt.label;
          return (
            <MenuItem key={value} value={value}>{text}</MenuItem>
          );
        })}
      </TextField>
    );
    return wrapCell(field, { fullWidth: !!forceFull, key });
  };
  const cb = (label, key) => (
    <FormControlLabel
      key={key}
      control={(
        <Checkbox
          checked={!!fields[key]}
          onChange={onCheck(key)}
          disabled={readOnly}
          inputProps={{ 'data-field': key }}
        />
      )}
      label={label}
    />
  );
  const yesNoRadio = (label, key, opts = {}) => {
    const { required: isRequired = true, show = true, onSideEffect } = opts;
    if (!show) return null;
    const raw = fields[key];
    const ynVal = raw === true || raw === 'yes' ? 'yes' : raw === false || raw === 'no' ? 'no' : '';
    const setYn = (next) => {
      if (clearFieldError) clearFieldError(key);
      setF(key, next);
      if (typeof onSideEffect === 'function') onSideEffect(next);
    };
    const emsg = err(key);
    return (
      <FormControl
        key={key}
        id={packetFieldId(key)}
        error={!!emsg}
        margin="dense"
        fullWidth
        component="fieldset"
        sx={{ mb: 0.5 }}
        data-field={key}
      >
        <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>
          {label}
          {isRequired && reqStar}
        </FormLabel>
        <RadioGroup
          row
          value={ynVal}
          onChange={(e) => setYn(e.target.value)}
        >
          <FormControlLabel value="yes" control={<Radio size="small" disabled={readOnly} />} label="Yes" />
          <FormControlLabel value="no" control={<Radio size="small" disabled={readOnly} />} label="No" />
        </RadioGroup>
        {!!emsg && (
          <Typography variant="caption" color="error" component="div">
            {emsg}
          </Typography>
        )}
      </FormControl>
    );
  };
  const cbReq = (label, key, boxSx = {}, opts = {}) => (
    <Box
      key={key}
      id={packetFieldId(key)}
      data-field={key}
      sx={{
        ...(err(key) ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : {}),
        ...boxSx,
      }}
    >
      <FormControlLabel
        control={(
          <Checkbox
            checked={!!fields[key]}
            onChange={onCheck(key)}
            color={err(key) ? 'error' : 'default'}
            disabled={readOnly || !!opts.disabled}
            inputProps={{ 'data-field': key }}
          />
        )}
        label={(
          <>
            {label}
            {reqStar}
          </>
        )}
      />
      {!!err(key) && (
        <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>
          {err(key)}
        </Typography>
      )}
    </Box>
  );

  if (formId === 'form_07') {
    const parentCellHint = 'At least one parent cell phone is required (mother or father)';
    const setMotherRole = (role) => {
      if (clearFieldError) {
        clearFieldError('mother_row_primary');
        clearFieldError('mother_row_guardian');
      }
      setF('mother_row_primary', role === 'primary');
      setF('mother_row_guardian', role === 'guardian');
    };
    const setFatherRole = (role) => {
      if (clearFieldError) {
        clearFieldError('father_row_primary');
        clearFieldError('father_row_guardian');
      }
      setF('father_row_primary', role === 'primary');
      setF('father_row_guardian', role === 'guardian');
    };
    const genderVal = fields.child_gender === 'M' || fields.child_gender === 'male'
      ? 'M'
      : fields.child_gender === 'F' || fields.child_gender === 'female'
        ? 'F'
        : '';
    return (
      <>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Child</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        <FormControl margin="dense" fullWidth component="fieldset" data-field="child_gender">
          <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>Gender{reqStar}</FormLabel>
          <RadioGroup
            row
            value={genderVal}
            onChange={(e) => setF('child_gender', e.target.value)}
          >
            <FormControlLabel value="M" control={<Radio size="small" disabled={readOnly} />} label="Male" />
            <FormControlLabel value="F" control={<Radio size="small" disabled={readOnly} />} label="Female" />
          </RadioGroup>
        </FormControl>
        <Divider sx={{ my: 2 }} />
        {showMother && (
          <>
            <Typography variant="subtitle2">Mother / guardian contact</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Check one: this row is the mother or a guardian filling in mother contact info.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={!!fields.mother_row_primary}
                    onChange={() => setMotherRole('primary')}
                    disabled={readOnly}
                  />
                )}
                label="Mother"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={!!fields.mother_row_guardian}
                    onChange={() => setMotherRole('guardian')}
                    disabled={readOnly}
                  />
                )}
                label="Guardian"
              />
            </Box>
            {row('Mother name', 'mother_name', {}, { required: true })}
            {row(
              'Mother cell',
              'mother_cell',
              err('mother_cell') ? {} : { helperText: parentCellHint },
              { phone: true },
            )}
            {row('Mother work address', 'mother_work_address', { multiline: true, minRows: 2 })}
            {row('Mother work phone', 'mother_work_phone', {}, { phone: true })}
          </>
        )}
        {showFather && (
          <>
            <Typography variant="subtitle2" sx={{ mt: showMother ? 1 : 0 }}>Father / guardian contact</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={!!fields.father_row_primary}
                    onChange={() => setFatherRole('primary')}
                    disabled={readOnly}
                  />
                )}
                label="Father"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={!!fields.father_row_guardian}
                    onChange={() => setFatherRole('guardian')}
                    disabled={readOnly}
                  />
                )}
                label="Guardian"
              />
            </Box>
            {row('Father name', 'father_name')}
            {row(
              'Father cell',
              'father_cell',
              (!showMother && !err('father_cell') && !err('mother_cell'))
                ? { helperText: parentCellHint }
                : {},
              { phone: true },
            )}
            {row('Father work address', 'father_work_address', { multiline: true, minRows: 2 })}
            {row('Father work phone', 'father_work_phone', {}, { phone: true })}
          </>
        )}
        {showGuardian && (
          <>
            <Typography variant="subtitle2" sx={{ mt: showMother || showFather ? 1 : 0 }}>Guardian</Typography>
            {row('Guardian name', 'guardian_name')}
            {row('Guardian cell', 'guardian_cell', {}, { phone: true })}
            {row('Guardian work phone', 'guardian_work_phone', {}, { phone: true })}
            {row('Guardian home phone', 'guardian_home_phone', {}, { phone: true })}
            {row('Guardian email', 'guardian_email')}
            {row('Guardian work address', 'guardian_work_address', { multiline: true, minRows: 2 })}
            {row('Relationship to child (e.g. grandmother, step-parent)', 'guardian_relationship')}
          </>
        )}
        {row('Home address', 'home_address', { multiline: true, minRows: 2 })}
        {row('Home phone', 'home_phone', {}, { phone: true })}
        {row('Preferred phone', 'preferred_phone', {}, { phone: true, required: true })}
        {row('Alternate address or phone during program hours', 'alternate_address', { multiline: true, minRows: 2 })}
        {row('Email', 'email', {}, { required: true })}
        {yesNoRadio('Is email checked daily?', 'email_viewed_daily', { required: false })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contact 1</Typography>
        {row('Name', 'ec1_name', {}, { required: true })}
        {row('Relationship', 'ec1_relationship')}
        {row('Address', 'ec1_address', { multiline: true, minRows: 2 })}
        {row('Phone', 'ec1_phone', {}, { phone: true, required: true })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contact 2</Typography>
        {row('Name', 'ec2_name', {}, { required: true })}
        {row('Relationship', 'ec2_relationship')}
        {row('Address', 'ec2_address', { multiline: true, minRows: 2 })}
        {row('Phone', 'ec2_phone', {}, { phone: true, required: true })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Health information</Typography>
        {row('Allergies', 'allergies', { multiline: true, minRows: 2 })}
        {row('Medication at school', 'medication_school')}
        {row('Medication at home', 'medication_home')}
        {row('Pediatrician name', 'pediatrician_name')}
        {row('Pediatrician phone', 'pediatrician_phone', {}, { phone: true })}
        {row('Dentist name', 'dentist_name')}
        {row('Dentist phone', 'dentist_phone', {}, { phone: true })}
        {row('Specialist name (optional)', 'specialist_name')}
        {row('Specialist specialty', 'specialist_specialty')}
        {row('Specialist phone', 'specialist_phone', {}, { phone: true })}
        {row('Preferred hospital', 'preferred_hospital')}
        {row('Insurance ID (optional — enter N/A if not applicable)', 'insurance_id')}
        {row('Insurance type', 'insurance_type')}
      </>
    );
  }
  if (formId === 'form_08') {
    const gtubeYes = fields.gtube === 'yes' || fields.gtube === true;
    const seizureYes = fields.seizure === 'yes' || fields.seizure === true;
    const foodByMouthYes = fields.can_food_by_mouth === 'yes' || fields.can_food_by_mouth === true;
    const problemsYes = fields.problems_eating_drinking === 'yes';
    const setGtube = (next) => {
      if (clearFieldError) {
        clearFieldError('gtube');
        clearFieldError('gtube_feeding_instructions');
        clearFieldError('can_food_by_mouth');
        clearFieldError('can_food_by_mouth_liquids');
        clearFieldError('can_food_by_mouth_solids');
        clearFieldError('swallow_study');
      }
      setF('gtube', next);
      if (next === 'no') {
        setF('gtube_feeding_instructions', '');
        setF('can_food_by_mouth', '');
        setF('can_food_by_mouth_liquids', '');
        setF('can_food_by_mouth_solids', '');
        setF('swallow_study', '');
      }
    };
    const gtubeRadio = (
      <FormControl
        key="gtube"
        id={packetFieldId('gtube')}
        error={!!err('gtube')}
        margin="dense"
        fullWidth
        component="fieldset"
        sx={{ mb: 0.5 }}
        data-field="gtube"
      >
        <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>
          G-tube
          {reqStar}
        </FormLabel>
        <RadioGroup
          row
          value={fields.gtube === true || fields.gtube === 'yes' ? 'yes' : fields.gtube === false || fields.gtube === 'no' ? 'no' : ''}
          onChange={(e) => setGtube(e.target.value)}
        >
          <FormControlLabel value="yes" control={<Radio size="small" disabled={readOnly} />} label="Yes" />
          <FormControlLabel value="no" control={<Radio size="small" disabled={readOnly} />} label="No" />
        </RadioGroup>
        {!!err('gtube') && (
          <Typography variant="caption" color="error" component="div">
            {err('gtube')}
          </Typography>
        )}
      </FormControl>
    );
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Mark Yes or No for each medical condition.
        </Typography>
        {yesNoRadio('Visual impairment', 'visual_impairment')}
        {yesNoRadio('Glasses', 'glasses')}
        {yesNoRadio('History of ear infection', 'ear_infection')}
        {yesNoRadio('Ear tubes', 'ear_tubes')}
        {yesNoRadio('Hearing impairment', 'hearing_impairment')}
        {yesNoRadio('Assistive hearing device', 'assistive_hearing_device', {
          onSideEffect: (next) => {
            if (next === 'no') {
              setF('assistive_hearing_right', false);
              setF('assistive_hearing_left', false);
              if (clearFieldError) {
                clearFieldError('assistive_hearing_right');
                clearFieldError('assistive_hearing_left');
              }
            }
          },
        })}
        {(fields.assistive_hearing_device === 'yes' || fields.assistive_hearing_device === true) && (
          <Box sx={{ pl: +2, mb: 1 }}>
            {cb('Right ear', 'assistive_hearing_right')}
            {cb('Left ear', 'assistive_hearing_left')}
          </Box>
        )}
        {yesNoRadio('Seizure history', 'seizure')}
        {seizureYes && row('If yes, indicate type and frequency', 'seizure_type_frequency', { multiline: true, minRows: 2 }, { required: true })}
        {gtubeRadio}
        {gtubeYes && (
          <>
            {row('G-tube type / feeding instructions', 'gtube_feeding_instructions', { multiline: true, minRows: 2 }, { required: true })}
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2">Can have food by mouth?</Typography>
            {yesNoRadio('Can have food by mouth', 'can_food_by_mouth')}
            {foodByMouthYes && (
              <Box sx={{ pl: 2 }}>
                {yesNoRadio('Liquids', 'can_food_by_mouth_liquids')}
                {yesNoRadio('Solids', 'can_food_by_mouth_solids')}
              </Box>
            )}
            {yesNoRadio('Swallow study completed', 'swallow_study')}
            <Typography variant="body2" color="text.secondary" sx={{ pl: 2, mb: 1 }}>
              Swallow Study completed (Please send a copy to the nurse)
            </Typography>
          </>
        )}
        {yesNoRadio('History of upper respiratory infection', 'upper_respiratory_infection')}
        {yesNoRadio('Asthma', 'asthma')}
        {yesNoRadio('Respiratory compromise', 'respiratory_compromise')}
        {yesNoRadio('Cardiac compromise', 'cardiac_compromise')}
        {yesNoRadio('Can have fluids', 'can_have_fluids')}
        {yesNoRadio('Receives nebulizer treatments', 'nebulizer_treatments')}
        {yesNoRadio('Can be exposed to the sun', 'can_sun_exposure')}
        {yesNoRadio('History of diarrhea', 'history_diarrhea')}
        {yesNoRadio('History of constipation', 'history_constipation')}
        {yesNoRadio('History of elbow/shoulder dislocation', 'dislocation_history')}
        {yesNoRadio('Wears brace/orthotic', 'wears_brace_orthotic')}
        {yesNoRadio('Receives wound care/bandage changes', 'wound_care')}
        {yesNoRadio('Wears helmet during physical activity', 'wears_helmet')}
        {yesNoRadio('Skin condition', 'skin_condition')}
        {yesNoRadio('Dental condition', 'dental_condition')}
        {yesNoRadio('Tracheostomy', 'tracheostomy')}
        {yesNoRadio('Oxygen', 'oxygen')}
        {yesNoRadio('Requires oximeter monitoring', 'oximeter_monitoring')}
        <Divider sx={{ my: 1.5 }} />
        {yesNoRadio('Problems with eating or drinking', 'problems_eating_drinking')}
        {problemsYes && row('If yes, specify', 'problems_eating_drinking_specify', { multiline: true, minRows: 2 }, { required: true })}
        <TextField
          fullWidth
          label="Medications"
          value={fields.medications || ''}
          onChange={(e) => setF('medications', e.target.value)}
          margin="normal"
          size="small"
          multiline
          minRows={2}
        />
        <TextField
          fullWidth
          label="Other medical needs"
          value={fields.other_medical || ''}
          onChange={(e) => setF('other_medical', e.target.value)}
          margin="normal"
          size="small"
          multiline
          minRows={2}
        />
        <TextField
          fullWidth
          label="Past surgeries or hospitalizations (what and when)"
          value={fields.past_hospitalizations || ''}
          onChange={(e) => setF('past_hospitalizations', e.target.value)}
          margin="normal"
          size="small"
          multiline
          minRows={2}
        />
      </>
    );
  }
  if (formId === 'form_09') {
    const rows = fields.allergy_rows || [];
    const allergyChoice = fields.has_no_allergies ? 'none' : fields.has_allergies ? 'has' : '';
    const setAllergyChoice = (choice) => {
      if (clearFieldError) {
        clearFieldError('has_allergies');
        clearFieldError('has_no_allergies');
      }
      setF('has_no_allergies', choice === 'none');
      setF('has_allergies', choice === 'has');
    };
    const dairyChoice = fields.dairy_no_milk_or_food
      ? 'no_milk_or_food'
      : fields.dairy_no_milk_some_food
        ? 'no_milk_some_food'
        : fields.dairy_avoid_occasional
          ? 'avoid_occasional'
          : '';
    const setDairyChoice = (choice) => {
      setF('dairy_no_milk_or_food', choice === 'no_milk_or_food');
      setF('dairy_no_milk_some_food', choice === 'no_milk_some_food');
      setF('dairy_avoid_occasional', choice === 'avoid_occasional');
    };
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        <FormControl error={!!err('has_allergies')} margin="dense" fullWidth component="fieldset" data-field="has_allergies" sx={{ mb: 1 }}>
          <FormLabel component="legend">Allergies or food restrictions{reqStar}</FormLabel>
          <RadioGroup row value={allergyChoice} onChange={(e) => setAllergyChoice(e.target.value)}>
            <FormControlLabel value="none" control={<Radio size="small" />} label="No known allergies" />
            <FormControlLabel value="has" control={<Radio size="small" />} label="Has allergies (list below)" />
          </RadioGroup>
          {!!err('has_allergies') && (
            <Typography variant="caption" color="error">{err('has_allergies')}</Typography>
          )}
        </FormControl>
        {[0, 1, 2, 3, 4].map((i) => (
          <Box
            key={i}
            id={i === 0 ? packetFieldId('allergy_rows') : undefined}
            data-field={i === 0 ? 'allergy_rows' : undefined}
            sx={{
              mb: 2,
              p: 1,
              border: '1px solid',
              borderColor: i === 0 && err('allergy_rows') ? 'error.main' : 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="caption">Allergen row {i + 1}</Typography>
            <TextField
              fullWidth
              size="small"
              label={fields.has_allergies && i === 0 ? (
                <>Allergen / restriction{reqStar}</>
              ) : 'Allergen / restriction'}
              value={rows[i]?.allergen || ''}
              onChange={(e) => {
                if (clearFieldError) clearFieldError('allergy_rows');
                const next = [...rows];
                next[i] = { ...next[i], allergen: e.target.value };
                setF('allergy_rows', next);
              }}
              error={i === 0 && !!err('allergy_rows')}
              helperText={i === 0 && err('allergy_rows') ? err('allergy_rows') : undefined}
              margin="dense"
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {['eat', 'touch', 'smell'].map((k) => (
                <FormControlLabel
                  key={k}
                  control={
                    <Checkbox
                      checked={!!rows[i]?.[k]}
                      onChange={(e) => {
                        const next = [...rows];
                        next[i] = { ...next[i], [k]: e.target.checked };
                        setF('allergy_rows', next);
                      }}
                    />
                  }
                  label={k}
                />
              ))}
            </Box>
            <FormControlLabel
              sx={{ mt: 0.5, display: 'block' }}
              control={
                <Checkbox
                  checked={!!rows[i]?.requires_anaphylaxis_plan}
                  onChange={(e) => {
                    const next = [...rows];
                    next[i] = { ...next[i], requires_anaphylaxis_plan: e.target.checked };
                    setF('allergy_rows', next);
                  }}
                  inputProps={{ 'data-field': i === 0 ? 'requires_anaphylaxis_plan' : undefined }}
                />
              }
              label="Food allergy / anaphylaxis risk — requires emergency plan"
            />
            <TextField
              fullWidth
              size="small"
              label="Reaction"
              value={rows[i]?.reaction || ''}
              onChange={(e) => {
                const next = [...rows];
                next[i] = { ...next[i], reaction: e.target.value };
                setF('allergy_rows', next);
              }}
              margin="dense"
            />
          </Box>
        ))}
        {selectRow('My child can only eat foods from home', 'food_from_home_yn', YES_NO_LOWER_OPTIONS, { required: true })}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
          My child can have food by mouth (solids)
        </Typography>
        <FormControlLabel
          control={(
            <Checkbox
              checked={!!fields.food_chewable_solids}
              onChange={onCheck('food_chewable_solids')}
              inputProps={{ 'data-field': 'food_chewable_solids' }}
            />
          )}
          label="Chewable solids"
        />
        <FormControlLabel
          control={(
            <Checkbox
              checked={!!fields.food_pureed}
              onChange={onCheck('food_pureed')}
              inputProps={{ 'data-field': 'food_pureed' }}
            />
          )}
          label="Pureed"
        />
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
          My child can have liquids
        </Typography>
        <FormControlLabel
          control={(
            <Checkbox
              checked={!!fields.food_liquid_thin}
              onChange={onCheck('food_liquid_thin')}
              inputProps={{ 'data-field': 'food_liquid_thin' }}
            />
          )}
          label="Thin (regular)"
        />
        <FormControlLabel
          control={(
            <Checkbox
              checked={!!fields.food_liquid_thickened}
              onChange={onCheck('food_liquid_thickened')}
              inputProps={{ 'data-field': 'food_liquid_thickened' }}
            />
          )}
          label="Thickened (honey thick, nectar thick, etc.)"
        />
        {row('Type of thickness (if thickened)', 'liquid_thickness_detail')}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
          Milk / dairy (check one if applicable)
        </Typography>
        <FormControl margin="dense" fullWidth component="fieldset">
          <RadioGroup value={dairyChoice} onChange={(e) => setDairyChoice(e.target.value)}>
            <FormControlLabel
              value="no_milk_or_food"
              control={<Radio size="small" />}
              label="Cannot drink milk or eat dairy foods (per MD orders)"
            />
            <FormControlLabel
              value="no_milk_some_food"
              control={<Radio size="small" />}
              label="Should not drink milk but can eat some dairy foods"
            />
            <FormControlLabel
              value="avoid_occasional"
              control={<Radio size="small" />}
              label="Try to avoid milk/dairy but child can have it on occasion"
            />
          </RadioGroup>
        </FormControl>
        {fields.dairy_no_milk_some_food && row('Dairy foods allowed', 'dairy_allowed_foods')}
        <TextField
          fullWidth
          id={packetFieldId('additional_comments')}
          data-field="additional_comments"
          label="Additional instructions or comments about allergies, food restrictions, or special diets"
          value={fields.additional_comments || ''}
          onChange={onText('additional_comments')}
          margin="normal"
          size="small"
          multiline
          minRows={3}
        />
      </>
    );
  }
  if (formId === 'form_10') {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        {row('Nickname', 'nickname')}
        {row('Hair color', 'hair_color', {}, { required: true })}
        {row('Eye color', 'eye_color', {}, { required: true })}
        {selectRow('Glasses', 'glasses', YES_NO_OPTIONS, { required: true })}
        {row('Physical description', 'physical_description', { multiline: true, minRows: 3 }, { required: true })}
        {row('Usual behavior', 'usual_behavior', { multiline: true, minRows: 2 }, { required: true })}
        {row('Communication ability', 'communication_ability', { multiline: true, minRows: 2 }, { required: true })}
        {row('Favorite locations', 'favorite_locations', {}, { required: true })}
      </>
    );
  }
  if (formId === 'form_11') {
    const pdfHref = `${API_BASE_URL}/packet/${encodeURIComponent(token)}/${encodeURIComponent(formId)}/pdf/`;
    const defaultChildName = `${packet.student_first_name || ''} ${packet.student_last_name || ''}`.trim();
    const childNameOnForm = (fields.student_name != null && String(fields.student_name).trim() !== '')
      ? fields.student_name
      : defaultChildName;
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {defaultChildName || 'Student'} — DOB {packet.student_dob || '—'}
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
          Over-the-counter medication consent
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          By submitting this page, you authorize HASC staff to administer over-the-counter medications
          to your child as directed by your child&apos;s pediatrician and documented on the signed medical form.
          This consent remains in effect for the current school year unless you submit an updated instruction.
        </Typography>
        <TextField
          label="Child's name on the printable form"
          value={childNameOnForm}
          onChange={(e) => setF('student_name', e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          helperText="Pre-filled from your child's record. Edit if the printed name should differ."
          inputProps={{ 'aria-label': "Child's name on the printable OTC form" }}
        />
        <Alert severity="info" sx={{ mb: 2 }}>
          Print this form and take it to your child&apos;s pediatrician. The doctor must enter dosages and sign
          before HASC can administer Tylenol or Benadryl. The child&apos;s name prints in normal-size type on the PDF.
        </Alert>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<PictureAsPdfIcon />}
            {...externalPdfLinkProps(pdfHref)}
          >
            View / print OTC medication form (PDF)
          </Button>
        </Box>
      </>
    );
  }
  if (formId === 'form_12') {
    const setCreamsAck = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) {
        clearFieldError('creams_consent_ack');
        clearFieldError('creams_consent_decline');
      }
      if (checked) setF('creams_consent_decline', false);
      setF('creams_consent_ack', checked);
    };
    const setCreamsDecline = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) {
        clearFieldError('creams_consent_ack');
        clearFieldError('creams_consent_decline');
      }
      if (checked) setF('creams_consent_ack', false);
      setF('creams_consent_decline', checked);
    };
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
          Consent for use of creams and lotions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose whether you consent to HASC staff applying the topical lotions, creams, and ointments
          listed in the letter above on an as-needed basis for your child.
        </Typography>
        <Box
          id={packetFieldId('creams_consent_ack')}
          data-field="creams_consent_ack"
          sx={err('creams_consent_ack') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}
        >
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={!!fields.creams_consent_ack}
                onChange={setCreamsAck}
                disabled={readOnly}
                color={err('creams_consent_ack') ? 'error' : 'default'}
                inputProps={{ 'data-field': 'creams_consent_ack' }}
              />
            )}
            label="I consent to the use of creams and lotions as described above"
          />
          {!!err('creams_consent_ack') && (
            <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>
              {err('creams_consent_ack')}
            </Typography>
          )}
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5, color: 'error.main' }}>
          If you do not consent
        </Typography>
        <Box
          id={packetFieldId('creams_consent_decline')}
          data-field="creams_consent_decline"
          sx={err('creams_consent_decline') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}
        >
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={!!fields.creams_consent_decline}
                onChange={setCreamsDecline}
                disabled={readOnly}
                color={err('creams_consent_decline') ? 'error' : 'default'}
                inputProps={{ 'data-field': 'creams_consent_decline' }}
              />
            )}
            label="I DO NOT consent to the use of creams and lotions"
            sx={{ color: 'error.main' }}
          />
          {!!err('creams_consent_decline') && (
            <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>
              {err('creams_consent_decline')}
            </Typography>
          )}
        </Box>
        <TextField
          id={packetFieldId('creams_avoid_notes')}
          data-field="creams_avoid_notes"
          fullWidth
          size="small"
          label="If there is something to stay away from, please note it here (optional)"
          value={fields.creams_avoid_notes || ''}
          onChange={onText('creams_avoid_notes')}
          margin="normal"
          multiline
          minRows={2}
          disabled={readOnly}
        />
      </>
    );
  }
  if (formId === 'form_13') {
    const items = [
      ['c_classroom', 'Classroom use (projects, Smartboards, bulletin boards)'],
      ['c_newsletters', 'Newsletters to classmates'],
      ['c_hallways', 'Photo/video in hallways'],
      ['c_website', 'Website / informational / fundraising'],
      ['c_flyers', 'Brochures / flyers'],
      ['c_slideshow_building', 'Slideshow in school building'],
      ['c_class_list', 'Name on class list'],
      ['c_yearbook', 'End-of-year slideshow / yearbook'],
    ];
    // A2P two-box consent (2026-06-16-a2p-v1) replaced the 4 legacy comm_* boxes
    // (comm_sms / comm_email / comm_voice / comm_data_rates_ack) on 2026-07-27.
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        {items.map(([k, lab]) => (
          <FormControlLabel
            key={k}
            control={<Checkbox checked={!!fields[k]} onChange={(e) => setF(k, e.target.checked)} />}
            label={lab}
          />
        ))}
        <ParentPacketCommunicationConsentFields fields={fields} setF={setF} />
      </>
    );
  }
  if (formId === 'form_14') {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Child: {packet.student_first_name} {packet.student_last_name} — Parent: {packet.parent_name || '—'}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          List at least one person authorized to pick up your child (name, relationship, and phone required).
        </Typography>
        <Typography variant="subtitle2">Authorized pickup 1{reqStar}</Typography>
        {row('Name', 'p1_name', {}, { required: true })}
        {row('Relationship to child', 'p1_relationship', {}, { required: true })}
        {row('Phone', 'p1_phone', {}, { phone: true, required: true })}
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Authorized pickup 2</Typography>
        {row('Name', 'p2_name')}
        {row('Relationship to child', 'p2_relationship')}
        {row('Phone', 'p2_phone', {}, { phone: true })}
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Authorized pickup 3</Typography>
        {row('Name', 'p3_name')}
        {row('Relationship to child', 'p3_relationship')}
        {row('Phone', 'p3_phone', {}, { phone: true })}
      </>
    );
  }
  if (formId === 'form_15') {
    const setReleaseAck = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) {
        clearFieldError('release_consent_ack');
        clearFieldError('release_consent_decline');
      }
      if (checked) setF('release_consent_decline', false);
      setF('release_consent_ack', checked);
    };
    const setReleaseDecline = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) {
        clearFieldError('release_consent_ack');
        clearFieldError('release_consent_decline');
      }
      if (checked) setF('release_consent_ack', false);
      setF('release_consent_decline', checked);
    };
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — {packet.parent_name || '—'}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Choose one (required):
        </Typography>
        <Box
          id={packetFieldId('release_consent_ack')}
          data-field="release_consent_ack"
          sx={err('release_consent_ack') || err('release_consent_decline') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}
        >
          <FormControlLabel
            control={(
              <Checkbox
                checked={!!fields.release_consent_ack}
                onChange={setReleaseAck}
                color={err('release_consent_ack') ? 'error' : 'default'}
                inputProps={{ 'data-field': 'release_consent_ack' }}
              />
            )}
            label="I authorize the release of medical, educational, therapy, and prescription information as described above"
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={!!fields.release_consent_decline}
                onChange={setReleaseDecline}
                color={err('release_consent_decline') ? 'error' : 'default'}
                inputProps={{ 'data-field': 'release_consent_decline' }}
              />
            )}
            label="I DO NOT authorize this release"
          />
        </Box>
      </>
    );
  }
  if (formId === 'form_20' || formId === 'fa_18' || formId === 'wm_17' || formId === 'bs_17') {
    const setPhiAuthorize = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) {
        clearFieldError('phi_authorize');
        clearFieldError('phi_decline');
      }
      if (checked) setF('phi_decline', false);
      setF('phi_authorize', checked);
    };
    const setPhiDecline = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) {
        clearFieldError('phi_authorize');
        clearFieldError('phi_decline');
      }
      if (checked) setF('phi_authorize', false);
      setF('phi_decline', checked);
    };
    const opwddVal = (fields.opwdd_eligible || '').toString().toLowerCase();
    const setOpwdd = (next) => {
      if (clearFieldError) clearFieldError('opwdd_eligible');
      setF('opwdd_eligible', next);
    };
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — {packet.parent_name || '—'}
        </Typography>
        <FormControl
          id={packetFieldId('opwdd_eligible')}
          error={!!err('opwdd_eligible')}
          margin="dense"
          fullWidth
          component="fieldset"
          sx={{ mb: 1 }}
          data-field="opwdd_eligible"
        >
          <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>
            Is your child eligible for OPWDD services?
            {reqStar}
          </FormLabel>
          <RadioGroup row value={opwddVal} onChange={(e) => setOpwdd(e.target.value)}>
            <FormControlLabel value="yes" control={<Radio size="small" disabled={readOnly} />} label="Yes" />
            <FormControlLabel value="no" control={<Radio size="small" disabled={readOnly} />} label="No" />
            <FormControlLabel
              value="unsure"
              control={<Radio size="small" disabled={readOnly} />}
              label="Unsure / I would like more information"
            />
          </RadioGroup>
          {!!err('opwdd_eligible') && (
            <Typography variant="caption" color="error">{err('opwdd_eligible')}</Typography>
          )}
        </FormControl>
        {yesNoRadio('Is your child currently receiving respite services?', 'receiving_respite')}
        {yesNoRadio('Would you like HASC to contact you about available after-school services?', 'contact_after_school')}
        <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
          Choose one (required):
          {reqStar}
        </Typography>
        <Box
          id={packetFieldId('phi_authorize')}
          data-field="phi_authorize"
          sx={err('phi_authorize') || err('phi_decline') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}
        >
          <FormControlLabel
            control={(
              <Checkbox
                checked={!!fields.phi_authorize}
                onChange={setPhiAuthorize}
                disabled={readOnly}
                color={err('phi_authorize') ? 'error' : 'default'}
                inputProps={{ 'data-field': 'phi_authorize' }}
              />
            )}
            label="I authorize HASC to disclose the information described above."
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={!!fields.phi_decline}
                onChange={setPhiDecline}
                disabled={readOnly}
                color={err('phi_decline') ? 'error' : 'default'}
                inputProps={{ 'data-field': 'phi_decline' }}
              />
            )}
            label="I do not authorize HASC to disclose the information described above."
          />
          {(!!err('phi_authorize') || !!err('phi_decline')) && (
            <Typography variant="caption" color="error" display="block">
              {err('phi_authorize') || err('phi_decline')}
            </Typography>
          )}
        </Box>
      </>
    );
  }
  if (formId === 'form_18') {
    return (
      <>
        <Typography variant="body2" color="text.secondary">
          {packet.student_first_name} {packet.student_last_name}
          {packet.student_dob && ` — DOB ${packet.student_dob}`}
        </Typography>
        {cbReq('I have read and understand the illness policy', 'confirm_policy', { mt: 2 })}
      </>
    );
  }
  if (formId === 'form_19') {
    const handbookHref = parentHandbookPdfHref({
      siteSlug: siteSlug || getPacketSiteSlug(packet),
      token,
      apiBaseUrl: API_BASE_URL,
      handbookUrl: packet?.handbook_url,
    });
    const is55th = (siteSlug || getPacketSiteSlug(packet)) === '55th-street';
    const ackCount = is55th ? 2 : 3;
    // Decision (WO 2026-07-22 #4): gate ALL acknowledgment checkboxes behind the handbook
    // click — not only box 1. Parents who skip the PDF would otherwise check boxes 2/3 and
    // still be blocked on box 1 with a confusing partial state.
    const handbookGated = !!handbookHref && !handbookOpened && !readOnly;
    const handleOpenHandbook = () => {
      // Navigation happens via the anchor href (mobile-safe); this just clears the gate.
      markHandbookOpened(token);
      setHandbookOpened(true);
    };
    return (
      <>
        <Typography variant="body2" color="text.secondary">
          {packet.student_first_name} {packet.student_last_name}
          {` — ${packet.parent_name || ''}`}
          {packet.student_dob && ` — DOB ${packet.student_dob}`}
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            This page requires {ackCount} acknowledgments below.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.75 }}>
            {handbookHref
              ? 'First click View Parent Handbook (PDF) — that unlocks the checkboxes. Then check each acknowledgment.'
              : 'Check each acknowledgment below to continue.'}
          </Typography>
        </Alert>
        {handbookHref && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant={handbookGated ? 'contained' : 'outlined'}
              color={handbookGated ? 'primary' : 'inherit'}
              size="medium"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleOpenHandbook}
              {...externalPdfLinkProps(handbookHref)}
            >
              View Parent Handbook (PDF)
            </Button>
          </Box>
        )}
        {handbookGated && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Open the Parent Handbook (PDF) above before you can check the acknowledgments.
          </Alert>
        )}
        <Box
          id={packetFieldId('handbook_received_ack')}
          data-field="handbook_received_ack"
          sx={{
            mt: 2,
            ...(err('handbook_received_ack')
              ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 }
              : {}),
          }}
        >
          <FormControlLabel
            sx={{ alignItems: 'flex-start', mr: 0 }}
            disabled={readOnly || handbookGated}
            control={(
              <Checkbox
                checked={!!fields.handbook_received_ack}
                onChange={(e) => {
                  if (clearFieldError) clearFieldError('handbook_received_ack');
                  setF('handbook_received_ack', e.target.checked);
                }}
                color={err('handbook_received_ack') ? 'error' : 'default'}
                disabled={readOnly || handbookGated}
                sx={{ pt: 0.25 }}
                inputProps={{ 'data-field': 'handbook_received_ack' }}
              />
            )}
            label={(
              <Typography variant="body2">
                I have received a copy of the HASC Parent Handbook. I reviewed the information, and I am aware that a file is maintained on my child and it is my right to view this file at any time.
                {!readOnly && <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>}
              </Typography>
            )}
          />
          {!!err('handbook_received_ack') && (
            <Typography color="error" variant="caption" component="div" sx={{ pl: 4, pb: 0.5 }}>
              {err('handbook_received_ack')}
            </Typography>
          )}
        </Box>
        {cbReq(
          'I have reviewed the Illness Policy, and understand its contents.',
          'handbook_illness_policy_ack',
          { mt: 1 },
          { disabled: handbookGated },
        )}
        {!is55th && cbReq(
          'I understand that the therapy team will make every effort or attempt to make up missed therapy sessions.',
          'handbook_therapy_makeup_ack',
          { mt: 1 },
          { disabled: handbookGated },
        )}
        {!readOnly && (() => {
          const stillNeeded = [];
          if (handbookGated) {
            stillNeeded.push('Open View Parent Handbook (PDF) to unlock the checkboxes');
          } else {
            if (!fields.handbook_received_ack) {
              stillNeeded.push('Acknowledgment 1 — Parent Handbook receipt');
            }
            if (!fields.handbook_illness_policy_ack) {
              stillNeeded.push('Acknowledgment 2 — Illness Policy');
            }
            if (!is55th && !fields.handbook_therapy_makeup_ack) {
              stillNeeded.push('Acknowledgment 3 — therapy makeup sessions');
            }
          }
          if (!stillNeeded.length) return null;
          return (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Still needed before you can submit:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.75 }}>
                {stillNeeded.map((item) => (
                  <Typography key={item} component="li" variant="body2">
                    {item}
                  </Typography>
                ))}
              </Box>
            </Alert>
          );
        })()}
      </>
    );
  }

  if (formId === 'fa_03') {
    const rows = fields.bus_pickup_rows || [];
    const ensureRows = () => (rows.length ? [...rows] : Array(2).fill(null).map(() => ({})));
    const patchRow = (i, patch) => {
      const next = ensureRows();
      next[i] = { ...(next[i] || {}), ...patch };
      setF('bus_pickup_rows', next);
    };
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          List 2 authorized persons — first name, last name, and phone for each.
        </Typography>
        {[0, 1].map((i) => {
          const fa03k = (part) => `fa03_bus_${i}_${part}`;
          return (
          <Box key={i} sx={{ mb: 2, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="caption">Authorized person {i + 1}</Typography>
            <TextField
              fullWidth
              size="small"
              required
              id={packetFieldId(fa03k('first'))}
              inputProps={{ 'data-field': fa03k('first') }}
              label="First name"
              value={rows[i]?.first_name || ''}
              onChange={(e) => {
                if (clearFieldError) clearFieldError(fa03k('first'));
                patchRow(i, { first_name: e.target.value });
              }}
              margin="dense"
              error={!!err(fa03k('first'))}
              helperText={err(fa03k('first'))}
            />
            <TextField
              fullWidth
              size="small"
              required
              id={packetFieldId(fa03k('last'))}
              inputProps={{ 'data-field': fa03k('last') }}
              label="Last name"
              value={rows[i]?.last_name || ''}
              onChange={(e) => {
                if (clearFieldError) clearFieldError(fa03k('last'));
                patchRow(i, { last_name: e.target.value });
              }}
              margin="dense"
              error={!!err(fa03k('last'))}
              helperText={err(fa03k('last'))}
            />
            <TextField
              fullWidth
              size="small"
              label="Relationship (optional)"
              value={rows[i]?.relationship || ''}
              onChange={(e) => patchRow(i, { relationship: e.target.value })}
              margin="dense"
            />
            <TextField
              fullWidth
              size="small"
              label="Address (optional)"
              margin="dense"
              value={rows[i]?.address || ''}
              onChange={(e) => patchRow(i, { address: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              required
              id={packetFieldId(fa03k('phone'))}
              inputProps={{ 'data-field': fa03k('phone') }}
              label="Phone"
              value={rows[i]?.phone || ''}
              onChange={(e) => {
                if (clearFieldError) clearFieldError(fa03k('phone'));
                patchRow(i, { phone: e.target.value });
              }}
              margin="dense"
              error={!!err(fa03k('phone'))}
              helperText={err(fa03k('phone')) || '10 digits required'}
            />
          </Box>
          );
        })}
      </>
    );
  }

  if (formId === 'fa_04') {
    // 55th Street requires all three emergency contacts; 14th Ave only EC1 (plus any started EC2/3).
    const is55thFa04 = (siteSlug || getPacketSiteSlug(packet)) === '55th-street';
    const parentCellHint = 'At least one parent cell phone is required (mother or father)';
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        {showMother && (
          <>
            {row('Mother name', 'mother_name')}
            {row(
              'Mother cell',
              'mother_cell',
              err('mother_cell') ? {} : { helperText: parentCellHint },
              { phone: true },
            )}
          </>
        )}
        {showFather && (
          <>
            {row('Father name', 'father_name')}
            {row(
              'Father cell',
              'father_cell',
              (!showMother && !err('father_cell') && !err('mother_cell'))
                ? { helperText: parentCellHint }
                : {},
              { phone: true },
            )}
          </>
        )}
        {showGuardian && (
          <>
            <Typography variant="subtitle2" sx={{ mt: showMother || showFather ? 1 : 0 }}>Guardian</Typography>
            {row('Guardian name', 'guardian_name')}
            {row('Guardian cell', 'guardian_cell', {}, { phone: true })}
            {row('Guardian work phone', 'guardian_work_phone', {}, { phone: true })}
            {row('Guardian home phone', 'guardian_home_phone', {}, { phone: true })}
            {row('Guardian email', 'guardian_email')}
            {row('Guardian work address', 'guardian_work_address', { multiline: true, minRows: 2 })}
            {row('Relationship to child (e.g. grandmother, step-parent)', 'guardian_relationship')}
          </>
        )}
        {row('Home address', 'home_address', { multiline: true, minRows: 2 })}
        {row('Email', 'email', {}, { required: true })}
        {row('Allergies', 'allergies', {
          multiline: true,
          minRows: 2,
          ...(err('allergies') ? {} : { helperText: "If no known allergies, write 'None'." }),
        }, { required: true })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contacts</Typography>
        {row('Contact 1 name', 'ec1_name', {}, { required: true })}
        {row('Relationship', 'ec1_relationship', {}, { required: true })}
        {row('Phone', 'ec1_phone', {}, { phone: true, required: true })}
        {row('Contact 2 name', 'ec2_name', {}, { required: is55thFa04 })}
        {row('Relationship', 'ec2_relationship', {}, { required: is55thFa04 })}
        {row('Phone', 'ec2_phone', {}, { phone: true, required: is55thFa04 })}
        {row('Contact 3 name', 'ec3_name', {}, { required: is55thFa04 })}
        {row('Relationship', 'ec3_relationship', {}, { required: is55thFa04 })}
        {row('Phone', 'ec3_phone', {}, { phone: true, required: is55thFa04 })}
        {row('Pediatrician', 'pediatrician_name', {}, { required: true })}
        {row('Pediatrician phone', 'pediatrician_phone', {}, { phone: true, required: true })}
        {row('Dentist', 'dentist_name')}
        {row('Dentist phone', 'dentist_phone', {}, { phone: true })}
        {row('Preferred hospital', 'preferred_hospital')}
        {row('Insurance ID (optional — enter N/A if not applicable)', 'insurance_id')}
        {row('Insurance type', 'insurance_type')}
        {row('Medications at school', 'medications_school', { multiline: true, minRows: 2 })}
        {row('Additional health notes', 'health_additional_notes', { multiline: true, minRows: 3 })}
        {cbReq('I consent to emergency medical treatment as described in the form above', 'emergency_treatment_ack', { mt: 2 })}
      </>
    );
  }

  if (formId === 'fa_05') {
    return (
      <>
        <Typography variant="subtitle2" sx={{ mt: 0.5 }}>Child (correct if needed)</Typography>
        {row('Legal first name', 'child_first_name')}
        {row('Legal last name', 'child_last_name')}
        {row('Date of birth (YYYY-MM-DD)', 'child_dob')}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Student resides with (check all that apply)</Typography>
        {cb('Parent(s)', 'resides_parents')}
        {row('Name(s) of parent(s)', 'resides_parents_names')}
        {cb('Guardian', 'resides_guardian')}
        {row('Guardian name', 'guardian_name')}
        {cb('Residence (name of residence)', 'resides_residence')}
        {row('Residence name', 'residence_name')}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Student address</Typography>
        {row('Street address', 'student_address', { multiline: true, minRows: 2 })}
        {row('City', 'student_city')}
        {selectRow('State', 'student_state', US_STATE_OPTIONS)}
        {row('ZIP', 'student_zip')}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Parent / guardian contact</Typography>
        {showMother && (
          <>
            {row('Mother name', 'mother_name')}
            {row('Mother cell', 'mother_cell', {}, { phone: true })}
          </>
        )}
        {showFather && (
          <>
            {row('Father name', 'father_name')}
            {row('Father cell', 'father_cell', {}, { phone: true })}
          </>
        )}
        {showGuardian && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Guardian (completing this packet)</Typography>
            {row('Guardian cell', 'guardian_cell', {}, { phone: true })}
            {row('Guardian work phone', 'guardian_work_phone', {}, { phone: true })}
            {row('Guardian home phone', 'guardian_home_phone', {}, { phone: true })}
            {row('Guardian email', 'guardian_email')}
            {row('Guardian work address', 'guardian_work_address', { multiline: true, minRows: 2 })}
            {row('Relationship to child', 'guardian_relationship')}
          </>
        )}
        {row('Email', 'email')}
        {row('Primary phone (form line)', 'parent_phone', {}, { phone: true })}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Insurance</Typography>
        {row('Medicaid # (optional — enter N/A if not applicable)', 'medicaid_number')}
        {row('Other health insurance — company', 'other_insurance_company')}
        {row('Other health insurance — policy # (optional — enter N/A if not applicable)', 'other_insurance_policy')}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Diagnoses (parent attestation)</Typography>
        {row('Physical diagnosis', 'parent_physical_diagnosis')}
        {row('Intellectual diagnosis', 'parent_intellectual_diagnosis')}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Allergies (parent attestation)</Typography>
        {row('Food', 'allergy_food')}
        {row('Drug', 'allergy_drug')}
        {row('Other', 'allergy_other', { multiline: true, minRows: 2 })}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Emergency contacts</Typography>
        {row('Contact 1 name', 'ec1_name')}
        {row('Relationship', 'ec1_relationship')}
        {row('Phone', 'ec1_phone', {}, { phone: true })}
        {row('Contact 2 name', 'ec2_name')}
        {row('Relationship', 'ec2_relationship')}
        {row('Phone', 'ec2_phone', {}, { phone: true })}
        {row('Contact 3 name', 'ec3_name')}
        {row('Relationship', 'ec3_relationship')}
        {row('Phone', 'ec3_phone', {}, { phone: true })}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Over-the-counter medication consent</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Page 2 of the medical form lists OTC medications your doctor may authorize: Tylenol/Advil,
          Benadryl PRN for allergic reaction, Tums/Pepto Bismol, Imodium, and Miralax. By consenting
          below, you authorize HASC staff to give these medications as your doctor orders on the
          printed form (your ink signature is on that page).
        </Typography>
        {cbReq('I consent to OTC medications as described above', 'otc_consent_ack', { mt: 0.5 })}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5, color: 'error.main' }}>
          If you do not consent
        </Typography>
        <Box id={packetFieldId('otc_consent_decline')} sx={err('otc_consent_decline') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={!!fields.otc_consent_decline}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (clearFieldError) { clearFieldError('otc_consent_ack'); clearFieldError('otc_consent_decline'); }
                  if (checked) setF('otc_consent_ack', false);
                  setF('otc_consent_decline', checked);
                }}
                disabled={readOnly}
                color={err('otc_consent_decline') ? 'error' : 'default'}
              />
            )}
            label="I do NOT consent to over-the-counter medications at school"
            sx={{ color: 'error.main' }}
          />
          {!!err('otc_consent_decline') && (
            <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>{err('otc_consent_decline')}</Typography>
          )}
        </Box>
      </>
    );
  }

  if (formId === 'fa_06') {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name}
        </Typography>
        <NaToggle
          label="My child does not take medications or have GTube feedings at school"
          checked={!!fields.na_no_meds_or_feedings}
          onChange={(next) => setF('na_no_meds_or_feedings', next)}
          targets={['medication_notes']}
          setF={setF}
        />
        {!fields.na_no_meds_or_feedings && row('Medication notes (from school record)', 'medication_notes', { multiline: true, minRows: 2 })}
        {fields.na_no_meds_or_feedings && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Medication detail fields are not applicable. You can still submit with the policy acknowledgment below.
          </Typography>
        )}
        {cbReq('I understand medications must be in labeled prescription bottles', 'understands_labeled_bottles', { mt: 1 })}
      </>
    );
  }

  if (formId === 'fa_07') {
    return (
      <>
        {row('Pediatrician name', 'pediatrician_name', {}, { required: true })}
        {row('Pediatrician phone', 'pediatrician_phone', {}, { phone: true, required: true })}
        {row('Dentist name', 'dentist_name')}
        {row('Dentist phone', 'dentist_phone', {}, { phone: true })}
        {row('Preferred hospital', 'preferred_hospital')}
        {row('Insurance ID (optional — enter N/A if not applicable)', 'insurance_id')}
        {row('Insurance type', 'insurance_type')}
        <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>
          Consent for communication with student&apos;s doctors
        </Typography>
        {cbReq("I consent to communication with my child's doctors and healthcare providers", 'doctor_comm_ack', { mt: 0.5 })}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 0.5, color: 'error.main' }}>
          If you do not consent
        </Typography>
        <Box id={packetFieldId('doctor_comm_decline')} sx={err('doctor_comm_decline') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={!!fields.doctor_comm_decline}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (clearFieldError) { clearFieldError('doctor_comm_ack'); clearFieldError('doctor_comm_decline'); }
                  if (checked) setF('doctor_comm_ack', false);
                  setF('doctor_comm_decline', checked);
                }}
                disabled={readOnly}
                color={err('doctor_comm_decline') ? 'error' : 'default'}
              />
            )}
            label="I do NOT consent to communication with my child's doctors"
            sx={{ color: 'error.main' }}
          />
        </Box>
        <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>
          Consent to release information to authorized personnel and agencies
        </Typography>
        {cbReq('I consent to the release of information to authorized personnel and agencies', 'release_info_ack', { mt: 0.5 })}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 0.5, color: 'error.main' }}>
          If you do not consent
        </Typography>
        <Box id={packetFieldId('release_info_decline')} sx={err('release_info_decline') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={!!fields.release_info_decline}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (clearFieldError) { clearFieldError('release_info_ack'); clearFieldError('release_info_decline'); }
                  if (checked) setF('release_info_ack', false);
                  setF('release_info_decline', checked);
                }}
                disabled={readOnly}
                color={err('release_info_decline') ? 'error' : 'default'}
              />
            )}
            label="I do NOT consent to release of information"
            sx={{ color: 'error.main' }}
          />
        </Box>
      </>
    );
  }

  if (formId === 'fa_08') {
    const setAffirm = (key) => (e) => {
      const checked = e.target.checked;
      if (clearFieldError) clearFieldError('consent_none');
      if (checked) setF('consent_none', false);
      setF(key, checked);
    };
    const setNone = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) clearFieldError('consent_none');
      if (checked) {
        FA08_AFFIRM_KEYS.forEach((k) => setF(k, false));
      }
      setF('consent_none', checked);
    };
    return (
      <>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          I hereby consent to the following (check all that apply):
        </Typography>
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.consent_video} onChange={setAffirm('consent_video')} disabled={readOnly} />} label="Video taping" />
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.consent_video_behind} onChange={setAffirm('consent_video_behind')} disabled={readOnly} />} label="Video taping from behind so face is obscured" />
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.consent_audio} onChange={setAffirm('consent_audio')} disabled={readOnly} />} label="Audio taping" />
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.consent_photo} onChange={setAffirm('consent_photo')} disabled={readOnly} />} label="Photographing" />
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.consent_photo_blurred} onChange={setAffirm('consent_photo_blurred')} disabled={readOnly} />} label="Photographing with blurred face or from behind" />
        <FormControlLabel control={<Checkbox size="small" checked={!!fields.consent_social_media} onChange={setAffirm('consent_social_media')} disabled={readOnly} />} label="Use on website or social media (Instagram)" />
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: 'error.main' }}>
          If you do not consent to video or photography
        </Typography>
        <Box id={packetFieldId('consent_none')} sx={err('consent_none') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={!!fields.consent_none}
                onChange={setNone}
                disabled={readOnly}
                color={err('consent_none') ? 'error' : 'default'}
              />
            )}
            label={(
              <>
                I DO NOT consent to video or photography of my child
                {reqStar}
              </>
            )}
            sx={{ color: 'error.main' }}
          />
          {!!err('consent_none') && (
            <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>
              {err('consent_none')}
            </Typography>
          )}
        </Box>
      </>
    );
  }

  if (formId === 'bs_08') {
    const BS08_AFFIRM_KEYS = ['consent_video', 'consent_audio', 'consent_photo', 'consent_school_use', 'consent_website'];
    const setMediaConsent = (key) => (e) => {
      const checked = e.target.checked;
      if (clearFieldError) { clearFieldError('consent_video'); clearFieldError('consent_none'); }
      if (checked) setF('consent_none', false);
      setF(key, checked);
    };
    const setBs08None = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) { clearFieldError('consent_video'); clearFieldError('consent_none'); }
      if (checked) BS08_AFFIRM_KEYS.forEach((k) => setF(k, false));
      setF('consent_none', checked);
    };
    return (
      <>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Media consent options (check all that apply):
        </Typography>
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={!!fields.consent_video}
              onChange={setMediaConsent('consent_video')}
              disabled={readOnly}
              color={err('consent_video') ? 'error' : 'default'}
              inputProps={{ 'data-field': 'consent_video' }}
            />
          )}
          label="Video recording"
        />
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={!!fields.consent_audio}
              onChange={setMediaConsent('consent_audio')}
              disabled={readOnly}
              inputProps={{ 'data-field': 'consent_audio' }}
            />
          )}
          label="Audio recording"
        />
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={!!fields.consent_photo}
              onChange={setMediaConsent('consent_photo')}
              disabled={readOnly}
              inputProps={{ 'data-field': 'consent_photo' }}
            />
          )}
          label="Photographs"
        />
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={!!fields.consent_school_use}
              onChange={setMediaConsent('consent_school_use')}
              disabled={readOnly}
              inputProps={{ 'data-field': 'consent_school_use' }}
            />
          )}
          label="School use only — bulletin boards, class newsletter, graduation video, etc."
        />
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={!!fields.consent_website}
              onChange={setMediaConsent('consent_website')}
              disabled={readOnly}
              inputProps={{ 'data-field': 'consent_website' }}
            />
          )}
          label="Use on school website / public materials"
        />
        {!!err('consent_video') && (
          <Typography
            id={packetFieldId('consent_video')}
            variant="caption"
            color="error"
            component="div"
            sx={{ mt: 0.5 }}
          >
            {err('consent_video')}
          </Typography>
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: 'error.main' }}>
          If you do not consent
        </Typography>
        <Box id={packetFieldId('consent_none')} data-field="consent_none" sx={err('consent_none') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={!!fields.consent_none}
                onChange={setBs08None}
                disabled={readOnly}
                color={err('consent_none') ? 'error' : 'default'}
                inputProps={{ 'data-field': 'consent_none' }}
              />
            )}
            label="I DO NOT consent to video, audio recording, or photography of my child"
            sx={{ color: 'error.main' }}
          />
          {!!err('consent_none') && (
            <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>{err('consent_none')}</Typography>
          )}
        </Box>
      </>
    );
  }

  if (formId === 'fa_09') {
    const REMOTE_SERVICE_KEYS = ['remote_classroom', 'remote_speech', 'remote_ot', 'remote_pt', 'remote_counseling'];
    const setRemoteService = (key) => (e) => {
      const checked = e.target.checked;
      if (clearFieldError) { clearFieldError('remote_classroom'); clearFieldError('remote_learning_decline'); }
      if (checked) setF('remote_learning_decline', false);
      setF(key, checked);
    };
    const setRemoteDecline = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) { clearFieldError('remote_classroom'); clearFieldError('remote_learning_decline'); }
      if (checked) REMOTE_SERVICE_KEYS.forEach((k) => setF(k, false));
      setF('remote_learning_decline', checked);
    };
    return (
      <>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          I consent to have my child receive the following services via remote learning and teletherapy:
        </Typography>
        <Box id={packetFieldId('remote_classroom')} sx={err('remote_classroom') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}>
          <FormControlLabel control={<Checkbox size="small" checked={!!fields.remote_classroom} onChange={setRemoteService('remote_classroom')} disabled={readOnly} />} label="Remote Learning with classroom teacher" />
          <FormControlLabel control={<Checkbox size="small" checked={!!fields.remote_speech} onChange={setRemoteService('remote_speech')} disabled={readOnly} />} label="Speech Therapy with assigned provider" />
          <FormControlLabel control={<Checkbox size="small" checked={!!fields.remote_ot} onChange={setRemoteService('remote_ot')} disabled={readOnly} />} label="Occupational Therapy with assigned provider" />
          <FormControlLabel control={<Checkbox size="small" checked={!!fields.remote_pt} onChange={setRemoteService('remote_pt')} disabled={readOnly} />} label="Physical Therapy with assigned provider" />
          <FormControlLabel control={<Checkbox size="small" checked={!!fields.remote_counseling} onChange={setRemoteService('remote_counseling')} disabled={readOnly} />} label="Counseling Services with assigned provider" />
          {!!err('remote_classroom') && (
            <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>{err('remote_classroom')}</Typography>
          )}
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5, color: 'error.main' }}>
          If you do not consent
        </Typography>
        <FormControlLabel
          control={<Checkbox size="small" checked={!!fields.remote_learning_decline} onChange={setRemoteDecline} disabled={readOnly} />}
          label="I DO NOT consent to remote learning or teletherapy services"
          sx={{ color: 'error.main' }}
        />
      </>
    );
  }

  if (formId === 'fa_10') {
    const setTripsAck = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) { clearFieldError('trips_ack'); clearFieldError('trips_decline'); }
      if (checked) setF('trips_decline', false);
      setF('trips_ack', checked);
    };
    const setTripsDecline = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) { clearFieldError('trips_ack'); clearFieldError('trips_decline'); }
      if (checked) setF('trips_ack', false);
      setF('trips_decline', checked);
    };
    return (
      <>
        <Box id={packetFieldId('trips_ack')} sx={err('trips_ack') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}>
          <FormControlLabel
            control={<Checkbox size="small" checked={!!fields.trips_ack} onChange={setTripsAck} disabled={readOnly} color={err('trips_ack') ? 'error' : 'default'} />}
            label="I consent to school trips and neighborhood walks as described"
          />
          {!!err('trips_ack') && (
            <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>{err('trips_ack')}</Typography>
          )}
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5, color: 'error.main' }}>
          If you do not consent
        </Typography>
        <Box id={packetFieldId('trips_decline')} sx={err('trips_decline') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}>
          <FormControlLabel
            control={<Checkbox size="small" checked={!!fields.trips_decline} onChange={setTripsDecline} disabled={readOnly} color={err('trips_decline') ? 'error' : 'default'} />}
            label="I DO NOT consent to school trips and neighborhood walks"
            sx={{ color: 'error.main' }}
          />
          {!!err('trips_decline') && (
            <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>{err('trips_decline')}</Typography>
          )}
        </Box>
      </>
    );
  }

  if (formId === 'fa_11') {
    return (
      <>
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={!!fields.sc_not_assigned}
              onChange={(e) => {
                const checked = e.target.checked;
                if (clearFieldError) {
                  clearFieldError('sc_name');
                  clearFieldError('sc_agency');
                  clearFieldError('sc_phone');
                }
                setF('sc_not_assigned', checked);
                if (checked) {
                  setF('sc_name', '');
                  setF('sc_agency', '');
                  setF('sc_phone', '');
                }
              }}
              disabled={readOnly}
            />
          )}
          label="I have not been assigned a service coordinator yet"
          sx={{ mb: 1, display: 'block' }}
        />
        {!fields.sc_not_assigned && (
          <>
            {row('Service coordinator name (optional)', 'sc_name')}
            {row('Agency (optional)', 'sc_agency')}
            {row('Telephone (optional)', 'sc_phone', {}, { phone: true })}
          </>
        )}
      </>
    );
  }

  if (formId === 'fa_12') {
    return (
      <Fa12SocialHistoryFields
        fields={fields}
        setF={setF}
        packet={packet}
        fieldErrors={fieldErrors}
        clearFieldError={clearFieldError}
        readOnly={readOnly}
      />
    );
  }

  if (formId === 'fa_16') {
    return (
      <Fa16SocialHistoryFields
        fields={fields}
        setF={setF}
        packet={packet}
        fieldErrors={fieldErrors}
        clearFieldError={clearFieldError}
      />
    );
  }

  if (formId === 'fa_17') {
    return (
      <Fa17FeedingQuestionnaireFields
        fields={fields}
        setF={setF}
        fieldErrors={fieldErrors}
        clearFieldError={clearFieldError}
      />
    );
  }

  if (formId === 'wm_01') {
    return wmGrid(
      <>
        {row('Parent / guardian name', 'parent_name', {}, { required: true })}
        {row('Child name', 'student_name', {}, { required: true })}
        {row('Home phone', 'home_phone', {}, { phone: true })}
        {row('Cell phone', 'alert_cell_phone', {}, { required: true, phone: true })}
        {row('Email', 'email', {}, { required: true, fullWidth: true })}
      </>,
    );
  }

  if (formId === 'wm_02') {
    return wmGrid(
      <>
        {row('Parent / guardian name', 'parent_name')}
        {row('Medicaid CIN', 'medicaid_cin', {}, { required: false })}
      </>,
    );
  }

  if (formId === 'wm_13') {
    const setMedAck = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) { clearFieldError('medicaid_consent_ack'); clearFieldError('medicaid_consent_decline'); }
      if (checked) setF('medicaid_consent_decline', false);
      setF('medicaid_consent_ack', checked);
    };
    const setMedDecline = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) { clearFieldError('medicaid_consent_ack'); clearFieldError('medicaid_consent_decline'); }
      if (checked) setF('medicaid_consent_ack', false);
      setF('medicaid_consent_decline', checked);
    };
    return wmGrid(
      <>
        {row('Parent / guardian name', 'parent_name')}
        {row('Medicaid CIN', 'medicaid_cin', {}, { required: false })}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Medicaid Consent</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Please indicate whether you consent to the release of information to the Medicaid program.
        </Typography>
        <Box
          id={packetFieldId('medicaid_consent_ack')}
          data-field="medicaid_consent_ack"
          sx={err('medicaid_consent_ack') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}
        >
          <FormControlLabel
            control={<Checkbox size="small" checked={!!fields.medicaid_consent_ack} onChange={setMedAck} disabled={readOnly} color={err('medicaid_consent_ack') ? 'error' : 'default'} />}
            label="I consent to the release of information to the Medicaid program"
          />
          {!!err('medicaid_consent_ack') && (
            <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>{err('medicaid_consent_ack')}</Typography>
          )}
        </Box>
        <Box
          id={packetFieldId('medicaid_consent_decline')}
          data-field="medicaid_consent_decline"
          sx={err('medicaid_consent_decline') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}
        >
          <FormControlLabel
            control={<Checkbox size="small" checked={!!fields.medicaid_consent_decline} onChange={setMedDecline} disabled={readOnly} color={err('medicaid_consent_decline') ? 'error' : 'default'} />}
            label="I decline consent for the release of information to the Medicaid program"
            sx={{ color: 'error.main' }}
          />
          {!!err('medicaid_consent_decline') && (
            <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>{err('medicaid_consent_decline')}</Typography>
          )}
        </Box>
      </>,
    );
  }

  if (formId === 'wm_03') {
    const ecAddressFields = (n) => (
      <>
        {row('Address', `ec${n}_address`, { multiline: true, minRows: 2 })}
        {row('City', `ec${n}_city`)}
        {selectRow('State', `ec${n}_state`, US_STATE_OPTIONS)}
        {row('ZIP', `ec${n}_zip`)}
      </>
    );
    return wmGrid(
      <>
        {row('Student name', 'student_name')}
        {row('Date of birth', 'date_of_birth', { type: 'date' })}
        <FormControl error={!!err('gender')} margin="dense" fullWidth component="fieldset" sx={{ mb: 1 }} required>
          <Typography variant="caption" component="legend" sx={{ mb: 0.5 }}>
            Gender (M / F)
            <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>
          </Typography>
          <RadioGroup
            row
            value={fields.gender === 'male' || fields.gender === 'female' ? fields.gender : ''}
            onChange={(e) => {
              if (clearFieldError) clearFieldError('gender');
              setF('gender', e.target.value);
            }}
          >
            <FormControlLabel value="male" control={<Radio size="small" />} label="M" disabled={readOnly} />
            <FormControlLabel value="female" control={<Radio size="small" />} label="F" disabled={readOnly} />
          </RadioGroup>
          {!!err('gender') && (
            <Typography variant="caption" color="error">{err('gender')}</Typography>
          )}
        </FormControl>
        {row('Home address', 'home_address', { multiline: true, minRows: 2 }, { required: true })}
        {row('City', 'home_city', {}, { required: true })}
        {selectRow('State', 'home_state', US_STATE_OPTIONS, { required: true })}
        {row('ZIP', 'home_zip', {}, { required: true })}
        {row('Home phone', 'home_phone', {}, { phone: true })}
        {row('Child lives with', 'child_lives_with')}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Mother / guardian</Typography>
        {row('Mother name', 'mother_name')}
        {row('Mother cell', 'mother_cell', {}, { phone: true })}
        {row('Mother work phone', 'mother_work_phone', {}, { phone: true })}
        {row('Email', 'email')}
        {row('Mother work address', 'mother_work_address', { multiline: true, minRows: 2 })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Father / guardian</Typography>
        {row('Father name', 'father_name')}
        {row('Father cell', 'father_cell', {}, { phone: true })}
        {row('Father work phone', 'father_work_phone', {}, { phone: true })}
        {row('Father email', 'father_email')}
        {fields.email?.trim() && fields.father_email?.trim() && (
          <FormControl component="fieldset" sx={{ mt: 2 }} required>
            <FormLabel component="legend">
              Who should receive the DocuSeal signing email?
            </FormLabel>
            <RadioGroup
              row
              name="docuseal_recipient"
              value={fields.docuseal_recipient || ''}
              onChange={(e) => {
                if (clearFieldError) clearFieldError('docuseal_recipient');
                setF('docuseal_recipient', e.target.value);
              }}
            >
              <FormControlLabel value="mother" control={<Radio size="small" />} label="Mother" disabled={readOnly} />
              <FormControlLabel value="father" control={<Radio size="small" />} label="Father" disabled={readOnly} />
              <FormControlLabel value="both" control={<Radio size="small" />} label="Both" disabled={readOnly} />
            </RadioGroup>
            {err('docuseal_recipient') && (
              <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>
                {err('docuseal_recipient')}
              </Typography>
            )}
          </FormControl>
        )}
        {row('Father work address', 'father_work_address', { multiline: true, minRows: 2 })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Language and health</Typography>
        {row('Primary language', 'primary_language')}
        {row('Interpreter needed', 'interpreter_needed')}
        {row('Pediatrician name', 'pediatrician_name')}
        {row('Pediatrician phone', 'pediatrician_phone', {}, { phone: true })}
        {row('Allergies', 'allergies', {}, { fullWidth: true })}
        {row('Hospitalizations (if any)', 'hospitalizations', { multiline: true, minRows: 2 }, { required: false })}
        {row('Taking medications? (Yes/No)', 'medications_yesno')}
        {row('Medication details', 'medications_detail', { multiline: true, minRows: 2 })}
        {row('Insurance name', 'insurance_name')}
        {row('Insurance ID', 'insurance_id')}
        {row('Insurance group', 'insurance_group', {}, { fullWidth: true })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contact 1</Typography>
        {row('Name', 'ec1_name', {}, { required: true })}
        {row('Relationship', 'ec1_relationship')}
        {row('Home phone', 'ec1_home_phone', {}, { phone: true })}
        {row('Cell phone', 'ec1_cell_phone', {}, { phone: true, required: true })}
        {ecAddressFields(1)}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contact 2</Typography>
        {row('Name', 'ec2_name')}
        {row('Relationship', 'ec2_relationship')}
        {row('Home phone', 'ec2_home_phone', {}, { phone: true })}
        {row('Cell phone', 'ec2_cell_phone', {}, { phone: true })}
        {ecAddressFields(2)}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contact 3</Typography>
        {row('Name', 'ec3_name')}
        {row('Relationship', 'ec3_relationship')}
        {row('Home phone', 'ec3_home_phone', {}, { phone: true })}
        {row('Cell phone', 'ec3_cell_phone', {}, { phone: true })}
        {ecAddressFields(3)}
      </>,
    );
  }

  if (formId === 'wm_04') {
    return wmGrid(
      <>
        {row('Child name', 'student_name', {}, { required: true })}
        {row('Address (street, city, state, ZIP)', 'home_address', { multiline: true, minRows: 2 }, { required: true })}
        {row('Telephone number', 'home_phone', {}, { phone: true })}
        {row('Medical coverage (insurance plan name)', 'insurance_name', {}, { required: true })}
        {row('Emergency contact', 'ec1_name', {}, { required: true })}
        {row('Relationship to child', 'ec1_relationship', {}, { required: true })}
        {row('Authorized signature name', 'parent_name', {}, { required: true, fullWidth: true })}
        <FormControl error={!!err('gender')} margin="dense" fullWidth component="fieldset" data-field="gender" sx={{ mb: 1, mt: 1 }}>
          <Typography variant="caption" component="legend" sx={{ mb: 0.5 }}>
            Gender (M / F)
            <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>
          </Typography>
          <RadioGroup
            row
            value={fields.gender || ''}
            onChange={(e) => {
              if (clearFieldError) clearFieldError('gender');
              setF('gender', e.target.value);
            }}
          >
            <FormControlLabel value="male" control={<Radio size="small" />} label="M" disabled={readOnly} />
            <FormControlLabel value="female" control={<Radio size="small" />} label="F" disabled={readOnly} />
          </RadioGroup>
          {!!err('gender') && (
            <Typography variant="caption" color="error">{err('gender')}</Typography>
          )}
        </FormControl>
      </>,
    );
  }

  if (formId === 'wm_05') {
    return wmGrid(
      <>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Media consent options
        </Typography>
        {cb('Video recording', 'consent_video')}
        {cb('Audio recording', 'consent_audio')}
        {cb('Photographs', 'consent_photo')}
        {cbReq('I do not consent to any video/audio/photo recording', 'consent_none', { mt: 1 })}
      </>,
    );
  }

  if (formId === 'wm_07') {
    const setNoMeds = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) {
        clearFieldError('medication_name');
        clearFieldError('no_medications');
      }
      setF('no_medications', checked);
      if (checked) setF('medication_name', '');
    };
    return wmGrid(
      <>
        {row('Student name', 'student_name')}
        {row('Date of birth', 'date_of_birth', { type: 'date' })}
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={!!fields.no_medications}
              onChange={setNoMeds}
              disabled={readOnly}
              inputProps={{ 'data-field': 'no_medications' }}
            />
          )}
          label="No medication"
          sx={{ mt: 1, display: 'block' }}
        />
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Medication 1</Typography>
        {row('Medication name', 'medication_name', {}, { required: !fields.no_medications, fullWidth: true })}
        {row('Dosage', 'medication_dosage', {}, { required: !fields.no_medications })}
        {row('Frequency', 'medication_frequency', {}, { required: !fields.no_medications })}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Medication 2 (optional)</Typography>
        {row('Medication name', 'medication_2_name', {}, { fullWidth: true })}
        {row('Dosage', 'medication_2_dosage')}
        {row('Frequency', 'medication_2_frequency')}
      </>,
    );
  }

  if (formId === 'wm_09') {
    const setMedAck09 = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) { clearFieldError('medicaid_consent_ack'); clearFieldError('medicaid_consent_decline'); }
      if (checked) setF('medicaid_consent_decline', false);
      setF('medicaid_consent_ack', checked);
    };
    const setMedDecline09 = (e) => {
      const checked = e.target.checked;
      if (clearFieldError) { clearFieldError('medicaid_consent_ack'); clearFieldError('medicaid_consent_decline'); }
      if (checked) setF('medicaid_consent_ack', false);
      setF('medicaid_consent_decline', checked);
    };
    return wmGrid(
      <>
        {row('Parent / guardian name', 'parent_name')}
        {row('Medicaid CIN', 'medicaid_cin', {}, { required: false })}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Medicaid Consent</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Please indicate whether you consent to the release of information to the Medicaid program.
        </Typography>
        <Box
          id={packetFieldId('medicaid_consent_ack')}
          data-field="medicaid_consent_ack"
          sx={err('medicaid_consent_ack') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}
        >
          <FormControlLabel
            control={<Checkbox size="small" checked={!!fields.medicaid_consent_ack} onChange={setMedAck09} disabled={readOnly} color={err('medicaid_consent_ack') ? 'error' : 'default'} />}
            label="I consent to the release of information to the Medicaid program"
          />
          {!!err('medicaid_consent_ack') && (
            <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>{err('medicaid_consent_ack')}</Typography>
          )}
        </Box>
        <Box
          id={packetFieldId('medicaid_consent_decline')}
          data-field="medicaid_consent_decline"
          sx={err('medicaid_consent_decline') ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 } : undefined}
        >
          <FormControlLabel
            control={<Checkbox size="small" checked={!!fields.medicaid_consent_decline} onChange={setMedDecline09} disabled={readOnly} color={err('medicaid_consent_decline') ? 'error' : 'default'} />}
            label="I decline consent for the release of information to the Medicaid program"
            sx={{ color: 'error.main' }}
          />
          {!!err('medicaid_consent_decline') && (
            <Typography variant="caption" color="error" component="div" sx={{ pl: 4, pb: 0.5 }}>{err('medicaid_consent_decline')}</Typography>
          )}
        </Box>
      </>,
    );
  }

  if (formId === 'wm_10') {
    const authBlock = (n) => (
      <>
        <Typography variant="subtitle2" sx={{ mt: n === 1 ? 1 : 2 }}>
          {`Authorized person ${n}${n === 1 ? '' : ' (optional)'}`}
        </Typography>
        {row('Name', `auth${n}_name`, {}, n === 1 ? { required: true } : {})}
        {row('Cell', `auth${n}_cell`, {}, n === 1 ? { required: true, phone: true } : { phone: true })}
        {row('Make / model of vehicle', `auth${n}_vehicle`, {}, n === 1 ? { required: true } : {})}
        {row('License plate', `auth${n}_plate`, {}, n === 1 ? { required: true } : {})}
      </>
    );
    return wmGrid(
      <>
        {row('Child last name', 'child_last_name', {}, { required: true })}
        {row('Child first name', 'child_first_name', {}, { required: true })}
        {row('Address', 'home_address', { multiline: true, minRows: 2 }, { required: true })}
        {row('Town', 'home_city', {}, { required: true })}
        {row('ZIP code', 'home_zip', {}, { required: true })}
        {row('Child date of birth', 'date_of_birth', { type: 'date' }, { required: true })}
        <FormControl error={!!err('gender')} margin="dense" fullWidth component="fieldset" data-field="gender" sx={{ mb: 1 }}>
          <Typography variant="caption" component="legend" sx={{ mb: 0.5 }}>
            Gender (M / F)
            <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>
          </Typography>
          <RadioGroup
            row
            value={fields.gender || ''}
            onChange={(e) => {
              if (clearFieldError) clearFieldError('gender');
              setF('gender', e.target.value);
            }}
          >
            <FormControlLabel value="male" control={<Radio size="small" />} label="M" disabled={readOnly} />
            <FormControlLabel value="female" control={<Radio size="small" />} label="F" disabled={readOnly} />
          </RadioGroup>
          {!!err('gender') && (
            <Typography variant="caption" color="error">{err('gender')}</Typography>
          )}
        </FormControl>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Parent / guardian contact</Typography>
        {showMother && (
          <>
            {row('Mother/guardian name', 'mother_name', {}, { required: true })}
            {row('Mother/guardian cell', 'mother_cell', {}, { required: true, phone: true })}
          </>
        )}
        {showFather && (
          <>
            {row('Father/guardian name', 'father_name', {}, { required: true })}
            {row('Father/guardian cell', 'father_cell', {}, { required: true, phone: true })}
          </>
        )}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Persons authorized to receive child (must show ID)</Typography>
        {authBlock(1)}
        {authBlock(2)}
        {authBlock(3)}
        {authBlock(4)}
      </>,
    );
  }

  if (formId === 'wm_11') {
    return wmGrid(
      <>
        {row('Student name', 'student_name', {}, { fullWidth: true })}
      </>,
    );
  }

  if (formId === 'wm_12') {
    return wmGrid(
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Complete every field below. For allergies, enter your child&apos;s allergies or type <strong>None</strong> if there are no known allergies.
        </Typography>
        {row('Student name', 'student_name', {}, { required: true })}
        {row('Date of birth', 'date_of_birth', { type: 'date' }, { required: true })}
        {row('Allergies (food, medicine, insects, environmental, etc.)', 'allergies', { multiline: true, minRows: 2 }, { required: true })}
        {row('Special diet / food restrictions', 'special_diet', { multiline: true, minRows: 2 })}
        {row('Significant medical / surgical information', 'medical_surgical', { multiline: true, minRows: 2 })}
        {row('Significant birthmarks / scars', 'birthmarks_scars', { multiline: true, minRows: 2 })}
      </>,
    );
  }

  if (formId === 'wm_14') {
    return wmGrid(
      <>
        {row('Student name', 'student_name')}
        {row('Date of birth', 'date_of_birth', { type: 'date' })}
      </>,
    );
  }

  return null;
}

export default function ParentPacketPage() {
  const { token } = useParams();
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [fields, setFields] = useState({});
  const [draftSavedAtMs, setDraftSavedAtMs] = useState(null);
  const fieldsBaselineJsonRef = useRef(null);
  const [familyPickerExpanded, setFamilyPickerExpanded] = useState(true);
  const [fsDraft, setFsDraft] = useState({
    is_single_parent: false,
    has_mother: true,
    has_father: true,
    has_guardian: false,
  });
  const [familySaving, setFamilySaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitFieldErrors, setSubmitFieldErrors] = useState([]);
  const [printingPacket, setPrintingPacket] = useState(false);
  const [printError, setPrintError] = useState('');
  const [deepLinkFormId, setDeepLinkFormId] = useState('');
  const [deepLinkMissing, setDeepLinkMissing] = useState(false);
  const formPanelRef = useRef(null);

  const clearFieldError = useCallback((key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const fetchPacket = useCallback(async () => {
    if (!token) return;
    setError('');
    setLoading(true);
    try {
      const wantParam = new URLSearchParams(window.location.search).get('form');
      const url = wantParam
        ? `${API_BASE_URL}/packet/${encodeURIComponent(token)}/?form=${encodeURIComponent(wantParam)}`
        : `${API_BASE_URL}/packet/${encodeURIComponent(token)}/`;
      const res = await fetch(url);
      if (!res.ok) {
        if (res.status === 404) throw new Error('invalid');
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to load');
      }
      const data = await res.json();
      setPacket(data);
      const forms = data.forms || [];
      let want = '';
      try {
        want = normalizePacketWantFormId(new URLSearchParams(window.location.search).get('form'));
      } catch {
        /* ignore */
      }
      setDeepLinkFormId(want);
      let targetIdx = -1;
      if (want) {
        targetIdx = forms.findIndex((f) => f.form_id === want);
        setDeepLinkMissing(targetIdx < 0);
      } else {
        setDeepLinkMissing(false);
      }
      if (targetIdx < 0) {
        targetIdx = forms.findIndex((f) => f.status !== 'signed');
      }
      setCurrentIndex(targetIdx >= 0 ? targetIdx : forms.length);
    } catch (e) {
      setPacket(null);
      setError(e.message === 'invalid' ? null : e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPacket();
  }, [fetchPacket]);

  useEffect(() => {
    if (!packet) return;
    setFsDraft(familyFlagsFromPacket(packet));
  }, [packet]);

  useEffect(() => {
    if (!token) return;
    try {
      if (sessionStorage.getItem(`packet_fs_collapsed_${token}`) === '1') {
        setFamilyPickerExpanded(false);
      } else {
        setFamilyPickerExpanded(true);
      }
    } catch {
      /* ignore */
    }
  }, [token]);

  const saveFamilyStructure = useCallback(async () => {
    if (!token || !packet) return;
    if (!fsDraft.has_mother && !fsDraft.has_father && !fsDraft.has_guardian) {
      setError('Select at least one of Mother, Father, or Guardian.');
      return;
    }
    setError('');
    setFamilySaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/packet/${encodeURIComponent(token)}/family-structure/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_single_parent: false,
          has_mother: fsDraft.has_mother,
          has_father: fsDraft.has_father,
          has_guardian: fsDraft.has_guardian,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to save family setup');
      }
      const updated = await fetch(`${API_BASE_URL}/packet/${encodeURIComponent(token)}/`).then((r) => r.json());
      setPacket(updated);
      setFamilyPickerExpanded(false);
      try {
        sessionStorage.setItem(`packet_fs_collapsed_${token}`, '1');
      } catch {
        /* ignore */
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setFamilySaving(false);
    }
  }, [token, packet, fsDraft]);

  const currentForm = packet?.forms?.[currentIndex];
  const formId = currentForm?.form_id;

  useEffect(() => {
    setFieldErrors({});
    setSubmitFieldErrors([]);
  }, [formId, currentIndex]);

  const packetSiteSlug = getPacketSiteSlug(packet);
  const naToggleEnabledForSite = isNaToggleEnabledSite(packetSiteSlug);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const savedFormDataKey =
    currentForm?.saved_form_data != null && typeof currentForm.saved_form_data === 'object'
      ? JSON.stringify(currentForm.saved_form_data)
      : '';

  useLayoutEffect(() => {
    if (!packet || !formId || !token) return;

    if (currentForm?.status === 'signed') {
      const init = initialFieldsForForm(formId, packet);
      if (savedFormDataKey) {
        try {
          const saved = JSON.parse(savedFormDataKey);
          if (saved && typeof saved === 'object') {
            let merged;
            if (formId === 'fa_12') {
              merged = { ...initialFa12FieldsFromPacket(saved), confirm_agreed: true };
            } else {
              merged = { ...init, ...saved, confirm_agreed: true };
            }
            if (formId === 'fa_03' && merged.bus_pickup_rows) {
              merged = { ...merged, bus_pickup_rows: normalizeFa03BusRows(merged.bus_pickup_rows) };
            }
            if (formId === 'fa_07') {
              merged = {
                ...merged,
                doctor_comm_ack: !!(saved.doctor_comm_ack ?? saved.fa_07_doctor_ack ?? saved.release_ack),
                release_info_ack: !!(saved.release_info_ack ?? saved.fa_07_release_ack ?? saved.release_ack),
              };
            }
            setFields(merged);
            fieldsBaselineJsonRef.current = JSON.stringify(merged);
            setDraftSavedAtMs(null);
            return;
          }
        } catch {
          /* ignore */
        }
      }
      setFields(init);
      fieldsBaselineJsonRef.current = JSON.stringify(init);
      setDraftSavedAtMs(null);
      return;
    }

    const init = initialFieldsForForm(formId, packet);
    const hasPF = formHasParentFields(currentForm);

    if (hasPF) {
      try {
        const raw = localStorage.getItem(packetDraftStorageKey(token, formId));
        if (raw) {
          const parsed = JSON.parse(raw);
          const formState = parsed && typeof parsed === 'object' ? parsed.formState : null;
          if (formState && typeof formState === 'object' && !Array.isArray(formState)) {
            let merged = { ...init, ...formState };
            if (formId === 'fa_03' && merged.bus_pickup_rows) {
              const norm = normalizeFa03BusRows(merged.bus_pickup_rows);
              while (norm.length < 5) {
                norm.push({ first_name: '', last_name: '', relationship: '', address: '', phone: '' });
              }
              merged = { ...merged, bus_pickup_rows: norm.slice(0, 2) };
            }
            setFields(merged);
            fieldsBaselineJsonRef.current = JSON.stringify(merged);
            const at = typeof parsed.savedAt === 'number' ? parsed.savedAt : null;
            setDraftSavedAtMs(at != null ? at : Date.now());
            return;
          }
        }
      } catch {
        /* ignore draft parse errors */
      }
    }

    if (savedFormDataKey) {
      try {
        const saved = JSON.parse(savedFormDataKey);
        if (saved && typeof saved === 'object') {
          let merged;
          if (formId === 'fa_12') {
            merged = { ...initialFa12FieldsFromPacket(saved), confirm_agreed: false };
          } else {
            merged = { ...init, ...saved, confirm_agreed: false };
          }
          if (formId === 'fa_03' && merged.bus_pickup_rows) {
            const norm = normalizeFa03BusRows(merged.bus_pickup_rows);
            while (norm.length < 5) {
              norm.push({ first_name: '', last_name: '', relationship: '', address: '', phone: '' });
            }
            merged = { ...merged, bus_pickup_rows: norm.slice(0, 2) };
          }
          if (formId === 'fa_07') {
            merged = {
              ...merged,
              doctor_comm_ack: !!(saved.doctor_comm_ack ?? saved.fa_07_doctor_ack ?? saved.release_ack),
              release_info_ack: !!(saved.release_info_ack ?? saved.fa_07_release_ack ?? saved.release_ack),
            };
          }
          setFields(merged);
          fieldsBaselineJsonRef.current = JSON.stringify(merged);
          setDraftSavedAtMs(null);
          return;
        }
      } catch {
        /* ignore */
      }
    }
    setFields(init);
    fieldsBaselineJsonRef.current = JSON.stringify(init);
    setDraftSavedAtMs(null);
  }, [packet, formId, currentIndex, currentForm, savedFormDataKey, token]);

  const setF = useCallback((k, v) => {
    setFields((prev) => {
      const path = Array.isArray(k) ? k : String(k).split('.');
      if (path.length <= 1) return { ...prev, [k]: v };
      const next = { ...prev };
      let cursor = next;
      path.forEach((part, idx) => {
        if (idx === path.length - 1) {
          cursor[part] = v;
          return;
        }
        const existing = cursor[part];
        cursor[part] = Array.isArray(existing) ? [...existing] : { ...(existing || {}) };
        cursor = cursor[part];
      });
      return next;
    });
  }, []);

  const hasParentFields = formHasParentFields(currentForm);
  const infoAckRequired = fourteenthAveInfoAckRequired(formId, packetSiteSlug);

  useEffect(() => {
    if (!token || !formId || !packet || currentForm?.status === 'signed') return;
    if (!hasParentFields) return;

    const key = packetDraftStorageKey(token, formId);
    const t = setTimeout(() => {
      const baseline = fieldsBaselineJsonRef.current;
      if (baseline == null) return;

      let serializedNow;
      try {
        serializedNow = JSON.stringify(fields);
      } catch {
        return;
      }
      if (serializedNow === baseline) {
        try {
          localStorage.removeItem(key);
        } catch {
          /* ignore */
        }
        setDraftSavedAtMs(null);
        return;
      }
      const savedAt = Date.now();
      try {
        localStorage.setItem(key, JSON.stringify({ savedAt, formState: fields }));
      } catch {
        return;
      }
      setDraftSavedAtMs(savedAt);
    }, 500);

    return () => clearTimeout(t);
  }, [fields, token, formId, packet, currentForm?.status, hasParentFields]);

  const canSubmit = useMemo(
    () =>
      !currentForm
      || currentForm.status === 'signed'
      || !hasParentFields
      || submitFieldsValid(formId, fields),
    [currentForm, hasParentFields, formId, fields],
  );

  const isFieldsDirty = useMemo(() => {
    const baseline = fieldsBaselineJsonRef.current;
    if (baseline == null) return false;
    try {
      return JSON.stringify(fields) !== baseline;
    } catch {
      return false;
    }
  }, [fields]);

  const buildFormPayload = () => {
    const { confirm_agreed, ...rest } = fields;
    if (formId === 'fa_12') {
      return { ...buildFa12SubmitPayload(rest), confirm_agreed: !!confirm_agreed };
    }
    if (formId === 'fa_07') {
      return {
        ...rest,
        confirm_agreed: !!confirm_agreed,
        doctor_comm_ack: !!rest.doctor_comm_ack,
        release_info_ack: !!rest.release_info_ack,
        fa_07_doctor_ack: !!rest.doctor_comm_ack,
        fa_07_release_ack: !!rest.release_info_ack,
      };
    }
    if (formId === 'form_07') {
      return {
        ...rest,
        preferred_phone: resolveForm07PreferredPhone(rest),
        confirm_agreed: !!confirm_agreed,
      };
    }
    return { ...rest, confirm_agreed: !!confirm_agreed };
  };

  const showValidationErrors = useCallback((res) => {
    setFieldErrors(res.fieldErrorMap);
    setSubmitFieldErrors(res.errors.map((e) => e.message));
    requestAnimationFrame(() => {
      const el = res.firstErrorId && document.getElementById(res.firstErrorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusTarget = el.querySelector?.('input:not([type="hidden"]), textarea, select');
        if (focusTarget && typeof focusTarget.focus === 'function') {
          focusTarget.focus();
        }
      }
    });
  }, []);

  const flushPacketDraft = useCallback(() => {
    if (!token || !formId || !packet) return;
    if (!hasParentFields) return;
    try {
      localStorage.setItem(
        packetDraftStorageKey(token, formId),
        JSON.stringify({ savedAt: Date.now(), formState: fields }),
      );
    } catch {
      /* ignore */
    }
  }, [token, formId, packet, hasParentFields, fields]);

  const advanceAfterSubmit = useCallback((updatedPacket) => {
    const forms = updatedPacket?.forms || [];
    if (!forms.length) return;
    if (forms.every((f) => f.is_complete === true)) {
      setCurrentIndex(forms.length);
      return;
    }
    setCurrentIndex(firstIncompleteFormIndex(forms));
  }, []);

  const handleGoBack = useCallback(() => {
    flushPacketDraft();
    setFieldErrors({});
    setSubmitFieldErrors([]);
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, [flushPacketDraft]);

  const handleSubmitForm = async () => {
    if (!packet || !packet.forms || submitting) return;
    const form = packet.forms[currentIndex];
    if (!form) return;
    const allDone = packet.forms.every((f) => f.is_complete === true);
    const revisitingSigned = form.status === 'signed';
    if (revisitingSigned && currentIndex >= packet.forms.length - 1 && allDone) {
      setCurrentIndex(packet.forms.length);
      return;
    }
    if (revisitingSigned && !hasParentFields && !infoAckRequired) {
      advanceAfterSubmit(packet);
      return;
    }
    if (hasParentFields || infoAckRequired) {
      const res = validateForSubmit(form.form_id, fields, packet);
      if (!res.ok) {
        showValidationErrors(res);
        return;
      }
      setFieldErrors({});
      setSubmitFieldErrors([]);
    }
    setSubmitting(true);
    setError('');
    try {
      const body = (hasParentFields || infoAckRequired)
        ? {
          form_data: hasParentFields
            ? buildFormPayload()
            : { confirm_agreed: !!fields.confirm_agreed },
        }
        : { form_data: {} };
      const url = `${API_BASE_URL}/packet/${encodeURIComponent(token)}/${encodeURIComponent(form.form_id)}/submit/`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (Array.isArray(data.errors) && data.errors.length) {
          const mappedErrors = data.errors.map((e, idx) => {
            const fieldKey = e?.field || '';
            const message = e?.message || data.detail || 'Failed to submit';
            return {
              id: fieldKey ? packetFieldId(fieldKey) : `packet-field-server-${idx}`,
              fieldKey,
              message,
            };
          });
          const fieldErrorMap = {};
          mappedErrors.forEach((e) => {
            if (e.fieldKey) fieldErrorMap[e.fieldKey] = e.message;
          });
          showValidationErrors({
            ok: false,
            errors: mappedErrors,
            fieldErrorMap,
            firstErrorId: mappedErrors[0]?.id || null,
          });
          return;
        }
        throw new Error(data.detail || 'Failed to submit');
      }
      if (formHasParentFields(form) && token) {
        try {
          localStorage.removeItem(packetDraftStorageKey(token, form.form_id));
        } catch {
          /* ignore */
        }
        setDraftSavedAtMs(null);
      }
      const updated = await fetch(`${API_BASE_URL}/packet/${encodeURIComponent(token)}/`).then((r) => r.json());
      setPacket(updated);
      advanceAfterSubmit(updated);
    } catch (e) {
      console.error('Parent packet submit failed', form.form_id, e);
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrintPacket = useCallback(async () => {
    if (!token || printingPacket) return;
    setPrintError('');
    setPrintingPacket(true);
    try {
      const url = `${API_BASE_URL}/packet/${encodeURIComponent(token)}/print/`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/pdf, application/json;q=0.9, */*;q=0.8',
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Your packet is not ready to print yet. Please complete all forms first.');
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      // Real anchor click (mobile-safe): window.open(blobUrl) leaves a blank tab
      // in Android WebView / in-app email browsers. The download attribute gives
      // the saved file a proper name on every platform.
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'parent_packet_complete.pdf';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      setPrintError(e?.message || 'Unable to open your packet copy right now. Please try again.');
    } finally {
      setPrintingPacket(false);
    }
  }, [token, printingPacket]);

  const scrollToFormPanel = useCallback(() => {
    requestAnimationFrame(() => {
      formPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const handleSelectForm = useCallback((index) => {
    setFamilyPickerExpanded(false);
    setCurrentIndex(index);
    scrollToFormPanel();
  }, [scrollToFormPanel]);

  useEffect(() => {
    if (loading || !packet?.forms?.length) return;
    if (currentIndex >= packet.forms.length) return;
    scrollToFormPanel();
  }, [currentIndex, loading, packet, scrollToFormPanel]);

  useEffect(() => {
    if (!packet?.forms?.length) return;
    const complete = packet.forms.every((f) => f.is_complete === true);
    if (complete) return;
    if (currentIndex >= packet.forms.length) {
      setCurrentIndex(firstIncompleteFormIndex(packet.forms));
    }
  }, [currentIndex, packet]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !packet) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          This link is invalid or has expired. Please contact the school.
        </Alert>
      </Box>
    );
  }

  if (!packet || !packet.forms || packet.forms.length === 0) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 3 }}>
        <Alert severity="warning">
          This link may have expired or the packet is not ready yet. Please contact the school
          to request a new link.
        </Alert>
      </Box>
    );
  }

  const total = packet.forms.length;
  const allSigned = packet.forms.every((f) => f.is_complete === true);
  const remainingForms = remainingIncompleteForms(packet.forms);

  if (deepLinkMissing && deepLinkFormId) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This link asked for an additional form ({deepLinkFormId}) that is not available on this
          packet yet. Please contact the school so they can resend the correct link.
        </Alert>
        <PhysicianFormsDownload
          token={token}
          siteSlug={packetSiteSlug}
          physicianForms={packet.physician_download_forms}
        />
        <HandbookDownloadCard siteSlug={packetSiteSlug} token={token} />
      </Box>
    );
  }

  if (allSigned) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 3, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 2, fontSize: '1.5rem' }}>
          Thank you!
        </Typography>
        <Typography sx={{ fontSize: '1.1rem', mb: 3 }}>
          All forms have been submitted.
        </Typography>
        {!!printError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {printError}
          </Alert>
        )}
        <PhysicianFormsDownload
          token={token}
          siteSlug={packetSiteSlug}
          physicianForms={packet.physician_download_forms}
        />
        <HandbookDownloadCard siteSlug={packetSiteSlug} token={token} />
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handlePrintPacket}
          disabled={printingPacket}
          sx={{ py: 1.5, px: 3, fontSize: '1rem' }}
        >
          {printingPacket ? 'Preparing PDF…' : 'Print / Save My Packet'}
        </Button>
      </Box>
    );
  }

  const informational = isInformationalPacketForm(formId);
  const textOnlyInfo = informational && informationalTextOnlyForm(formId);
  const formIsSigned = currentForm?.status === 'signed';
  const allFormsComplete = (packet?.forms || []).length > 0
    && (packet.forms || []).every((f) => f.is_complete === true);
  const allowResubmit = formIsSigned && (hasParentFields || infoAckRequired) && isFieldsDirty;
  const primaryForwardLabel = forwardButtonLabel({
    form: currentForm,
    hasParentFields: hasParentFields || infoAckRequired,
    submitting,
    allowResubmit,
  });
  const primaryForwardDisabled = isForwardDisabled({
    submitting,
    currentIndex,
    formsLength: total,
    form: currentForm,
    allowResubmit,
    allFormsComplete,
  })
    || (!allowResubmit && infoAckRequired && !formIsSigned && !fields.confirm_agreed)
    || (!allowResubmit && hasParentFields && !formIsSigned && !canSubmit);

  const formTextScroll = (currentForm.form_text || '').trim() ? (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Form letter
      </Typography>
      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          maxHeight: 400,
          overflowY: 'auto',
          fontSize: '14px',
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          color: 'text.primary',
        }}
      >
        {currentForm.form_text}
      </Box>
      <CalendarFormActions
        formId={formId}
        siteSlug={packetSiteSlug}
        token={token}
        calendarLinks={packet?.calendar_links}
        rocklandCalendarLinks={packet?.rockland_calendar_links}
      />
      <MealsFormNotice formId={formId} token={token} />
      <InformationalPdfActions formId={formId} siteSlug={packetSiteSlug} token={token} />
    </Paper>
  ) : null;

  const familyPickerBlock = (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      {!familyPickerExpanded ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2">
            <strong>Editing as:</strong>{' '}
            {familyStructureEditingLabel(familyFlagsFromPacket(packet))}
          </Typography>
          <Button size="small" onClick={() => setFamilyPickerExpanded(true)}>
            Change
          </Button>
        </Box>
      ) : (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Who completes this packet? (check all that apply)
          </Typography>
          <FormControlLabel
            control={(
              <Checkbox
                checked={!!fsDraft.has_mother}
                onChange={(e) => setFsDraft((d) => ({ ...d, has_mother: e.target.checked }))}
              />
            )}
            label="Mother"
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={!!fsDraft.has_father}
                onChange={(e) => setFsDraft((d) => ({ ...d, has_father: e.target.checked }))}
              />
            )}
            label="Father"
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={!!fsDraft.has_guardian}
                onChange={(e) => setFsDraft((d) => ({ ...d, has_guardian: e.target.checked }))}
              />
            )}
            label="Guardian"
          />
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={saveFamilyStructure}
              disabled={familySaving}
              sx={{
                bgcolor: HASC_BLUE.primary,
                '&:hover': { bgcolor: HASC_BLUE.primaryHover },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
              }}
            >
              {familySaving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              size="small"
              onClick={() => {
                setFsDraft(familyFlagsFromPacket(packet));
                setFamilyPickerExpanded(false);
              }}
            >
              Cancel
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );

  const draftSavedHint = hasParentFields && draftSavedAtMs != null ? (
    <Typography variant="caption" component="p" color="text.secondary" sx={{ m: 0 }}>
      Saved {new Date(draftSavedAtMs).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      })}
    </Typography>
  ) : null;

  const incompleteHint = hasParentFields && !allowResubmit && !canSubmit && submitFieldErrors.length === 0 ? (
    <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
      Complete the required items above to submit.
    </Typography>
  ) : null;

  const childLabel = [packet.student_first_name, packet.student_last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  const confirmLabel = textOnlyInfo
    ? confirmAgreedLabel(formId)
    : ((formId === 'fa_12' || formId === 'fa_16' || formId === 'fa_17')
      ? 'I have filled this out to the best of my ability.'
      : 'I have read the form above and confirm my entries are accurate');

  const desktopRail = (
    <PacketFormIndex
      forms={packet.forms}
      currentIndex={currentIndex}
      onSelect={handleSelectForm}
      token={token}
      siteSlug={packetSiteSlug}
      siteName={packet.site_name}
      variant="rail"
      brand={HASC_BLUE}
    />
  );
  const phoneRail = (
    <PacketFormIndex
      forms={packet.forms}
      currentIndex={currentIndex}
      onSelect={handleSelectForm}
      token={token}
      siteSlug={packetSiteSlug}
      siteName={packet.site_name}
      variant="default"
      brand={HASC_BLUE}
    />
  );

  return (
    <Box
      data-na-toggle-enabled={naToggleEnabledForSite ? '1' : '0'}
      data-site-slug={packetSiteSlug || 'unknown'}
    >
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mx: 2, mt: 2 }}>
          {error}
        </Alert>
      )}
      <ParentPacketLayout
        isDesktop={isDesktop}
        siteName={packet.site_name}
        siteSlug={packetSiteSlug}
        childName={childLabel || packet.student_first_name}
        schoolYear={packet.school_year}
        sessionType={packet.session_type}
        currentIndex={currentIndex}
        totalForms={total}
        progressRail={desktopRail}
        mobileProgressRail={phoneRail}
        topExtras={familyPickerBlock}
        onBack={handleGoBack}
        backDisabled={submitting || currentIndex === 0}
        onForward={handleSubmitForm}
        forwardDisabled={primaryForwardDisabled}
        forwardLabel={primaryForwardLabel}
        footerHint={incompleteHint}
        draftSavedLabel={draftSavedHint}
      >
        <PhysicianFormsDownload
          token={token}
          siteSlug={packetSiteSlug}
          physicianForms={packet.physician_download_forms}
          variant="hascBlue"
        />
        <HandbookDownloadCard siteSlug={packetSiteSlug} token={token} variant="hascBlue" />
        {remainingForms.length > 0 && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              remainingForms[0]
                && packet.forms.indexOf(remainingForms[0]) !== currentIndex ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      const idx = packet.forms.indexOf(remainingForms[0]);
                      if (idx >= 0) handleSelectForm(idx);
                    }}
                  >
                    Go to next required
                  </Button>
                ) : null
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {remainingForms.length === 1
                ? '1 form still required before your packet is complete'
                : `${remainingForms.length} forms still required before your packet is complete`}
            </Typography>
            <Typography variant="body2" component="div">
              {remainingForms
                .slice(0, 6)
                .map((f) => f.form_name || f.form_id)
                .join(' · ')}
              {remainingForms.length > 6
                ? ` · +${remainingForms.length - 6} more`
                : ''}
            </Typography>
          </Alert>
        )}
        <Box
          ref={formPanelRef}
          id="packet-form-panel"
          sx={{ scrollMarginTop: 96 }}
        >
          <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1.35rem', fontWeight: 700 }}>
            {currentForm.form_name}
          </Typography>
          {textOnlyInfo && (
            <>
              {currentForm.status === 'signed' && (
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  You have already completed this form. Scroll below to re-read the letter; use the buttons to open the calendar or other PDFs.
                </Typography>
              )}
              <Box
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  maxHeight: 400,
                  overflowY: 'auto',
                  mb: 3,
                  fontSize: '14px',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  color: 'text.primary',
                }}
              >
                {currentForm.form_text || 'Loading…'}
              </Box>
              <CalendarFormActions
                formId={formId}
                siteSlug={packetSiteSlug}
                token={token}
                calendarLinks={packet?.calendar_links}
                rocklandCalendarLinks={packet?.rockland_calendar_links}
              />
              <MealsFormNotice formId={formId} token={token} />
              <InformationalPdfActions formId={formId} siteSlug={packetSiteSlug} token={token} />
              {currentForm.status !== 'signed' && infoAckRequired && (
                <Box
                  id={packetFieldId('confirm_agreed')}
                  sx={{
                    mb: 2,
                    ...(fieldErrors.confirm_agreed
                      ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 }
                      : {}),
                  }}
                >
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={!!fields.confirm_agreed}
                        onChange={(e) => {
                          clearFieldError('confirm_agreed');
                          setF('confirm_agreed', e.target.checked);
                        }}
                        color={fieldErrors.confirm_agreed ? 'error' : 'primary'}
                      />
                    )}
                    label={(
                      <>
                        {confirmLabel}
                        <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>
                      </>
                    )}
                    sx={{ display: 'block' }}
                  />
                  {!!fieldErrors.confirm_agreed && (
                    <Typography variant="caption" color="error" component="div" sx={{ pl: 4 }}>
                      {fieldErrors.confirm_agreed}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
          {!informational && (
            <>
              {formTextScroll}
              <Fa05MedicalFormLink formId={formId} token={token} />
              {allowResubmit && (
                <Typography color="text.secondary" sx={{ mb: hasParentFields ? 2 : 0 }}>
                  You can update your responses below and save changes.
                </Typography>
              )}
              {hasParentFields && (
                <>
                  <Typography sx={{ mb: 1, fontWeight: 600 }}>Your responses</Typography>
                  {shouldShowRequiredFieldsCaption(hasParentFields, informational) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Fields marked with * are required.
                    </Typography>
                  )}
                  <FormFields
                    formId={formId}
                    fields={fields}
                    setF={setF}
                    packet={packet}
                    fieldErrors={fieldErrors}
                    clearFieldError={clearFieldError}
                    readOnly={false}
                    token={token}
                    siteSlug={packetSiteSlug}
                    fieldLayout={isDesktop ? 'wmTwoColumn' : 'stack'}
                  />
                  <Box
                    id={packetFieldId('confirm_agreed')}
                    sx={{
                      mt: 2,
                      ...(fieldErrors.confirm_agreed
                        ? { border: '1px solid', borderColor: 'error.main', borderRadius: 1, px: 1, py: 0.5 }
                        : {}),
                    }}
                  >
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={!!fields.confirm_agreed}
                          onChange={(e) => {
                            clearFieldError('confirm_agreed');
                            setF('confirm_agreed', e.target.checked);
                          }}
                          color={fieldErrors.confirm_agreed ? 'error' : 'primary'}
                        />
                      )}
                      label={(
                        <>
                          {confirmLabel}
                          <Box component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>*</Box>
                        </>
                      )}
                      sx={{ display: 'block' }}
                    />
                    {!!fieldErrors.confirm_agreed && (
                      <Typography variant="caption" color="error" component="div" sx={{ pl: 4 }}>
                        {fieldErrors.confirm_agreed}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
              {!hasParentFields && (
                <Typography sx={{ mb: 2 }}>
                  Review the letter above, then continue.
                </Typography>
              )}
              {hasParentFields && submitFieldErrors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitFieldErrors([])}>
                  {submitFieldErrors.map((msg, idx) => (
                    <Typography key={`${msg}-${idx}`} component="div" variant="body2">
                      {msg}
                    </Typography>
                  ))}
                </Alert>
              )}
            </>
          )}
        </Box>
      </ParentPacketLayout>
    </Box>
  );
}
